<template>
  <v-dialog 
    v-model="toggle" 
    transition="slide-y-reverse-transition"
    scrollable 
    fullscreen
  >
    <v-card 
      id="profile"
      class="sheet"
      dark
    >
      <v-toolbar
        dark
        :elevation="toolbarElevation"
        class="page-header flex-grow-0 flex-shrink-1"
      >
        <v-btn
          icon
          dark
          @click="close"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
        <v-spacer />
        <v-toolbar-title class="title">{{ $t('profile.title') }}</v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn
            @click.stop="toggleHelp(true, 'profile')"
            icon
            class="mr-n2"
          >
            <v-icon v-text="icons.mdiHelpCircleOutline" />
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <v-card-text
        id="profile-scroll"
        v-scroll:#profile-scroll="onScroll"
        ref="content"
        class="content pa-2 pb-12 flex-grow-1 flex-shrink-0"
      >
        <div 
          class="profile-header text-center py-4"
        >
          <div class="profile-img mb-8">
            <v-avatar 
              v-show="!toggleUpload"
              size="96"
              :loading="!ready"
              class="avatar"
            >
              <v-img
                v-if="fields.photo.value!=null&&fields.photo.value!=''"
                :src="fields.photo.value"
                class="photo"
              />
              <icon-base
                v-else
                width="96"
                height="96"
                name="avatar"
                color="white"
                class="avatar-default"
              >
                <avatar-default />
              </icon-base>
              <v-fab-transition>
                <v-btn
                  v-show="!toggleUpload"
                  class="btn-upload"
                  color="secondary darken-2"
                  fab
                  dark
                  small 
                  absolute
                  bottom
                  right
                  elevation="2"
                  @click="toggleCropper(true);"
                >
                  <v-icon>{{ icons.mdiPencil }}</v-icon>
                </v-btn>
              </v-fab-transition>
            </v-avatar>

            <v-fab-transition>
              <v-btn
                v-show="toggleUpload"
                class="btn-cancel"
                fab
                light
                small
                absolute
                bottom
                left
                elevation="1"
                @click="toggleCropper(false);"
              >
                <v-icon>{{ icons.mdiClose }}</v-icon>
              </v-btn>
            </v-fab-transition>

            <cropper
              v-show="toggleUpload"
              v-model="cropper"
              ref="cropper"
              :width="240"
              :height="240"
              :quality="2"
              :prevent-white-space="true"
              :zoom-speed="10"
              :disable-click-to-choose="true"
              :show-remove-button="false"
              :file-size-limit="10000000"
              accept="image/*" 
              capture="true"
              data-exif-orientation="1"
              placeholder=""
              @init="handleCropperInit"
              @new-image-drawn="handleNewImage"
              @file-type-mismatch="onFileTypeMismatch"
              @file-size-exceed="onFileSizeExceed"
            />

            <v-fab-transition>
              <v-btn
                v-show="toggleUpload"
                class="btn-ok darken-1"
                color="primary"
                fab
                light
                small
                absolute
                bottom
                right
                elevation="3"
                @click="prepareImgUpload();"
              >
                <v-icon>{{ icons.mdiCheck }}</v-icon>
              </v-btn>
            </v-fab-transition>
          </div>

          <div class="title text-h6 mt-4">
            {{ shortName }}
          </div>

          <m-field
            v-if="!signup&&!!profile.badges&&(!config?.profile || !('badges' in config.profile) || config.profile.badges)"
            outlined
            center
            dark
            :label="$t('profile.badges.title')"
            :hint="badge_view.mini ? 'profile.badges.hint' : null"
            class="badges-container mt-10 mx-4 rounded-lg"
          >
            <badges 
              :items="profile.badges.items"
              :mini="badge_view.mini"
              class="badges"
              @click="toggleBadges"
            />
            
          </m-field>

          <v-btn
            v-if="!signup&&!!helpContent?.referrals"
            outlined
            small
            color="grey"
            class="mt-4"
            @click="toggleView({ view: 'profile', subview: 'referrals' })"
          >
            {{ $t('referrals.title') }}
          </v-btn>

          <!-- <v-card 
            v-if="profile.club_id!=null"
            outlined
            class="d-inline-flex align-center px-2 text-overline secondary--text mt-4"
            @click="club_id.toggle=true; selectText($refs['club_id'], true, $t('general.club_id_copied'))"
          >
            ID Clube Mobees: 
            <span
              v-show="club_id.toggle"
              ref="club_id"
              class="font-weight-bold"
              style="text-transform: initial;"
            >
              {{ profile.club_id }}
            </span>
            <v-icon 
              v-show="!club_id.toggle"
              color="secondary"
              small
              class="ml-1"
            >
              {{ icons.mdiEye }}
            </v-icon>
          </v-card> -->
        </div> 

        <v-form
          ref="form"
          class="profile-form pb-12"
        >
          <v-list 
            flat
            two-line
            color="transparent"
          >
            <v-divider />
            <v-list-group 
              v-if="sectionState.personal?.toggle"
              v-model="sections.personal"
              ref="personal"
              :disabled="signup ? false : !recentAuth"
              :ripple="false"
              :class="[!sectionState.personal?.completed&&sectionState.personal?.required ? 'pending' : 'completed']"
              class="form-group"
              @click.native="toggleAuth(true, 'personal'); scrollToSection('personal')"
            >
              <template v-slot:appendIcon>
                <v-icon v-show="ready" class="section-icon">
                  {{ sectionState.personal?.icon }}
                </v-icon>
              </template>
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('profile.personal.title') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('profile.personal.subtitle') }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <div 
                class="form-section"
              >
                <div v-if="ready" class="section-content pa-4 pb-0">
                  <v-text-field
                    v-if="fields.fullname.toggle"
                    v-model="fields.fullname.value"
                    :loading="fields.fullname.loading"
                    :disabled="fields.fullname.disabled"
                    :label="$t(fields.fullname.label)"
                    :hint="fields.fullname.hint"
                    :rules="fields.fullname.rules"
                    :success="signup&&fields.fullname.success"
                    :error="fields.fullname.error"
                    @click.stop
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('fullname')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-if="fields.email.toggle"
                    v-model="fields.email.value"
                    :loading="fields.email.loading"
                    :disabled="fields.email.disabled"
                    :label="$t(fields.email.label)"
                    :persitent-hint="auth.sms"
                    :rules="fields.email.rules"
                    :success="signup&&fields.email.success"
                    :error="fields.email.error"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('email')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-if="fields.cpf.toggle"
                    v-model="fields.cpf.value"
                    v-mask="fields.cpf.mask"
                    :loading="fields.cpf.loading"
                    :disabled="fields.cpf.disabled"
                    :label="$t(fields.cpf.label)"
                    :hint="fields.cpf.hint"
                    :rules="fields.cpf.rules"
                    :success="signup&&fields.cpf.success"
                    :error="fields.cpf.error"
                    type="tel"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('cpf')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-if="fields.rg.id.toggle"
                    v-model="fields.rg.id.value"
                    :loading="fields.rg.id.loading"
                    :disabled="fields.rg.id.disabled"
                    :label="$t(fields.rg.id.label)"
                    :rules="fields.rg.id.rules"
                    :success="signup&&fields.rg.id.success"
                    :error="fields.rg.id.error"
                    type="tel"
                    maxlength="11"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('rg.id')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-if="fields.rg.issuer.toggle"
                    v-model="fields.rg.issuer.value"
                    :loading="fields.rg.issuer.loading"
                    :disabled="fields.rg.issuer.disabled"
                    :label="$t(fields.rg.issuer.label)"
                    :rules="fields.rg.issuer.rules"
                    :success="signup&&fields.rg.issuer.success"
                    :error="fields.rg.issuer.error"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('rg.issuer')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-if="fields.cnh.toggle"
                    v-model="fields.cnh.value"
                    :loading="fields.cnh.loading"
                    :disabled="fields.cnh.disabled"
                    :label="$t(fields.cnh.label)"
                    :rules="fields.cnh.rules"
                    :success="signup&&fields.cnh.success"
                    :error="fields.cnh.error"
                    type="tel"
                    maxlength="11"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('cnh')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-if="fields.phone.toggle"
                    v-model="fields.phone.value"
                    v-mask="fields.phone.mask"
                    :loading="fields.phone.loading"
                    :disabled="fields.phone.disabled"
                    :label="$t(fields.phone.label)"
                    :hint="fields.phone.hint"
                    :rules="fields.phone.rules"
                    :success="signup&&fields.phone.success"
                    :error="fields.phone.error"
                    type="tel"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('phone')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-if="fields.birthdate.toggle"
                    v-model="fields.birthdate.value"
                    v-mask="fields.birthdate.mask"
                    :loading="fields.birthdate.loading"
                    :disabled="fields.birthdate.disabled"
                    :label="$t(fields.birthdate.label)"
                    :hint="fields.birthdate.hint"
                    :rules="fields.birthdate.rules"
                    :success="signup&&fields.birthdate.success"
                    :error="fields.birthdate.error"
                    type="tel"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('birthdate')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-select
                    v-if="fields.gender.toggle"
                    v-model="fields.gender.value"
                    :items="translate(fields.gender.items)"
                    :loading="fields.gender.loading"
                    :disabled="fields.gender.disabled"
                    :label="$t(fields.gender.label)"
                    :rules="fields.gender.rules"
                    :success="signup&&fields.gender.success"
                    :error="fields.gender.error"
                    :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @change="profileChanged('gender')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-select>

                  <v-text-field
                    v-if="fields.address.zip.toggle"
                    v-model="fields.address.zip.value"
                    v-mask="fields.address.zip.mask"
                    :loading="fields.address.zip.loading"
                    :disabled="fields.address.zip.disabled"
                    :label="$t(fields.address.zip.label)"
                    :hint="fields.address.zip.hint"
                    :rules="fields.address.zip.rules"
                    :success="signup&&fields.address.zip.success"
                    :error="fields.address.zip.error"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('address.zip')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-if="fields.address.street.toggle"
                    v-model="fields.address.street.value"
                    :loading="fields.address.street.loading"
                    :disabled="fields.address.street.disabled"
                    :label="$t(fields.address.street.label)"
                    :hint="fields.address.street.hint"
                    :rules="fields.address.street.rules"
                    :success="signup&&fields.address.street.success"
                    :error="fields.address.street.error"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('address.street')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-row 
                    v-if="fields.address.number.toggle&&fields.address.compl.toggle"
                    class="my-0"
                  >
                    <v-col cols="6" class="py-0">
                      <v-text-field
                        v-if="fields.address.number.toggle"
                        v-model="fields.address.number.value"
                        v-mask="fields.address.number.mask"
                        :loading="fields.address.number.loading"
                        :disabled="fields.address.number.disabled"
                        :label="$t(fields.address.number.label)"
                        :rules="fields.address.number.rules"
                        :success="signup&&fields.address.number.success"
                        :error="fields.address.number.error"
                        type="tel"
                        outlined
                        color="secondary"
                        class="control"
                        @focus="onFormFocus(true)"
                        @blur="onFormFocus(false)"
                        @input="profileChanged('address.number')"
                      >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>
                    </v-col>
                    <v-col class="py-0">
                      <v-text-field
                        v-if="fields.address.compl.toggle"
                        v-model="fields.address.compl.value"
                        :loading="fields.address.compl.loading"
                        :disabled="fields.address.compl.disabled"
                        :label="$t(fields.address.compl.label)"
                        :hint="fields.address.compl.hint"
                        :rules="fields.address.compl.rules"
                        :success="signup&&fields.address.compl.success"
                        :error="fields.address.compl.error"
                        :maxlength="50"
                        outlined
                        color="secondary"
                        class="control"
                        @focus="onFormFocus(true)"
                        @blur="onFormFocus(false)"
                        @input="profileChanged('address.compl')"
                      >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>
                    </v-col>
                  </v-row>

                  <v-text-field
                    v-if="fields.address.neighborhood.toggle"
                    v-model="fields.address.neighborhood.value"
                    :loading="fields.address.neighborhood.loading"
                    :disabled="fields.address.neighborhood.disabled"
                    :label="$t(fields.address.neighborhood.label)"
                    :hint="fields.address.neighborhood.hint"
                    :rules="fields.address.neighborhood.rules"
                    :success="signup&&fields.address.neighborhood.success"
                    :error="fields.address.neighborhood.error"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('address.neighborhood')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-if="fields.address.city.toggle"
                    v-model="fields.address.city.value"
                    :loading="fields.address.city.loading"
                    :disabled="fields.address.city.disabled"
                    :label="$t(fields.address.city.label)"
                    :hint="fields.address.city.hint"
                    :rules="fields.address.city.rules"
                    :success="signup&&fields.address.city.success"
                    :error="fields.address.city.error"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('address.city')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-select
                    v-if="fields.address.state.toggle"
                    v-model="fields.address.state.value"
                    :items="fields.address.state.items"
                    :loading="fields.address.state.loading"
                    :disabled="fields.address.state.disabled"
                    :label="$t(fields.address.state.label)"
                    :hint="fields.address.state.hint"
                    :rules="fields.address.state.rules"
                    :success="signup&&fields.address.state.success"
                    :error="fields.address.state.error"
                    :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @change="profileChanged('address.state')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-select>

                  <v-text-field
                    v-if="fields.address.country.toggle"
                    v-model="fields.address.country.value"
                    :loading="fields.address.country.loading"
                    :disabled="fields.address.country.disabled"
                    :label="$t(fields.address.country.label)"
                    :hint="fields.address.country.hint"
                    :rules="fields.address.country.rules"
                    :success="signup&&fields.address.country.success"
                    :error="fields.address.country.error"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('address.country')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>
                </div>
              </div>
            </v-list-group>

            <v-divider v-if="sectionState.vehicle?.toggle" />
            <v-list-group 
              v-if="sectionState.vehicle?.toggle"
              v-model="sections.vehicle"
              ref="vehicle"
              :disabled="signup ? false : !recentAuth"
              :ripple="false"
              :class="[!sectionState.vehicle?.completed&&sectionState.vehicle?.required ? 'pending' : 'completed']"
              class="form-group"
              @click.native="toggleAuth(true, 'vehicle'); scrollToSection('vehicle')"
            >
              <template v-slot:appendIcon>
                <v-icon v-show="ready" class="section-icon">
                  {{ sectionState.vehicle?.icon }}
                </v-icon>
              </template>
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('profile.vehicle.title') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('profile.vehicle.subtitle') }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <div 
                class="form-section"
              >
                <div v-if="ready" class="section-content pa-4 pb-0">
                  <!-- <v-combobox
                    v-if="fields.vehicle.brand.toggle"
                    v-model="profile.vehicle.brand"
                    :items="fields.vehicle.brand.items"
                    :loading="fields.vehicle.brand.loading"
                    :disabled="fields.vehicle.brand.disabled"
                    :label="$t(fields.vehicle.brand.label)"
                    :hint="fields.vehicle.brand.hint"
                    @change="profileChanged('vehicle.brand')"
                    @input="profileChanged('vehicle.brand')"
                    outlined
                    color="secondary"
                    class="control"
                  /> -->

                  <v-select
                    v-if="fields.vehicle.model.toggle"
                    v-model="fields.vehicle.model.value"
                    :items="vehicleList"
                    :loading="fields.vehicle.model.loading"
                    :disabled="fields.vehicle.model.disabled"
                    :label="$t(fields.vehicle.model.label)"
                    :hint="fields.vehicle.model.hint"
                    :rules="fields.vehicle.model.rules"
                    :success="signup&&fields.vehicle.model.success"
                    :error="fields.vehicle.model.error"
                    :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                    single-line
                    persistent-hint
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('vehicle.model')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-select>
                  <!-- <template v-slot:no-data>
                    <v-list-item @click="profile.vehicle.model='Outro'">
                      <v-list-item-content>
                        <v-list-item-title>
                          Outro
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template> -->

                  <v-select
                    v-if="fields.vehicle.color.toggle"
                    v-model="fields.vehicle.color.value"
                    :items="translate(fields.vehicle.color.items, 'text', 'profile.vehicle.color.')"
                    :loading="fields.vehicle.color.loading"
                    :disabled="fields.vehicle.color.disabled"
                    :label="$t(fields.vehicle.color.label)"
                    :hint="fields.vehicle.color.hint"
                    :rules="fields.vehicle.color.rules"
                    :success="signup&&fields.vehicle.color.success"
                    :error="fields.vehicle.color.error"
                    :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('vehicle.color')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-select>

                  <m-select
                    v-if="fields.vehicle.fuel.toggle"
                    :value="fields.vehicle.fuel.value"
                    :options="translate(fields.vehicle.fuel.items, 'text', 'profile.vehicle.fuel.')"
                    :loading="fields.vehicle.fuel.loading"
                    :disabled="fields.vehicle.fuel.disabled"
                    :label="$t(fields.vehicle.fuel.label)"
                    :hint="fields.vehicle.fuel.hint"
                    :rules="fields.vehicle.fuel.rules"
                    :success="signup&&fields.vehicle.fuel.success"
                    :error="fields.vehicle.fuel.error"
                    dark
                    multiple
                    outlined
                    vertical
                    color="secondary"
                    class="control"
                    @change="(value) => setField('vehicle.fuel', value)"
                  />

                  <v-text-field
                    v-if="fields.vehicle.plate.toggle"
                    v-model="fields.vehicle.plate.value"
                    :key="'plate-' + plateMaskKey"
                    v-mask="plateMaskDirective"
                    :loading="fields.vehicle.plate.loading"
                    :disabled="fields.vehicle.plate.disabled"
                    :label="$t(fields.vehicle.plate.label)"
                    :hint="fields.vehicle.plate.hint"
                    :rules="fields.vehicle.plate.rules"
                    :success="signup&&fields.vehicle.plate.success"
                    :error="fields.vehicle.plate.error"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="onPlateInput"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-if="fields.vehicle.chassis.toggle"
                    v-model="fields.vehicle.chassis.value"
                    :loading="fields.vehicle.chassis.loading"
                    :disabled="fields.vehicle.chassis.disabled"
                    :label="$t(fields.vehicle.chassis.label)"
                    :hint="fields.vehicle.chassis.hint"
                    :rules="fields.vehicle.chassis.rules"
                    :success="signup&&fields.vehicle.chassis.success"
                    :error="fields.vehicle.chassis.error"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('vehicle.chassis')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-if="fields.vehicle.renavam.toggle"
                    v-model="fields.vehicle.renavam.value"
                    :loading="fields.vehicle.renavam.loading"
                    :disabled="fields.vehicle.renavam.disabled"
                    :label="$t(fields.vehicle.renavam.label)"
                    :rules="fields.vehicle.renavam.rules"
                    :success="signup&&fields.vehicle.renavam.success"
                    :error="fields.vehicle.renavam.error"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('vehicle.renavam')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-if="fields.vehicle.year.toggle"
                    v-model="fields.vehicle.year.value"
                    :loading="fields.vehicle.year.loading"
                    :disabled="fields.vehicle.year.disabled"
                    :label="$t(fields.vehicle.year.label)"
                    :hint="fields.vehicle.year.hint"
                    :rules="fields.vehicle.year.rules"
                    :success="signup&&fields.vehicle.year.success"
                    :error="fields.vehicle.year.error"
                    type="tel"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('vehicle.year')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-select
                    v-if="fields.vehicle.parking.toggle"
                    v-model="fields.vehicle.parking.value"
                    :items="translate(fields.vehicle.parking.items)"
                    :loading="fields.vehicle.parking.loading"
                    :disabled="fields.vehicle.parking.disabled"
                    :label="$t(fields.vehicle.parking.label)"
                    :hint="fields.vehicle.parking.hint"
                    :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                    :rules="fields.vehicle.parking.rules"
                    :success="signup&&fields.vehicle.parking.success"
                    :error="fields.vehicle.parking.error"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @change="profileChanged('vehicle.parking')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-select>

                  <v-select
                    v-if="fields.vehicle.isOwner.toggle"
                    v-model="fields.vehicle.isOwner.value"
                    :items="translate(fields.vehicle.isOwner.items)"
                    :loading="fields.vehicle.isOwner.loading"
                    :disabled="fields.vehicle.isOwner.disabled"
                    :label="$t(fields.vehicle.isOwner.label)"
                    :hint="fields.vehicle.isOwner.hint"
                    :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                    :rules="fields.vehicle.isOwner.rules"
                    :success="signup&&fields.vehicle.isOwner.success"
                    :error="fields.vehicle.isOwner.error"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @change="profileChanged('vehicle.isOwner'); updateRequiredFields();"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-select>
                  <!-- <v-list-item
                    v-if="fields.vehicle.isOwner.toggle"
                    class="mt-n2 px-3"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ fields.vehicle.isOwner.label }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-switch 
                        v-model="profile.vehicle.isOwner" 
                        :loading="fields.vehicle.isOwner.loading"
                        :disabled="fields.vehicle.isOwner.disabled"
                        @change="profileChanged('vehicle.isOwner'); updateRequiredFields('vehicle', 'ownerRelative', !profile.vehicle.isOwner)"
                        inset
                        color="secondary"
                        class="control"
                      />
                    </v-list-item-action>
                  </v-list-item> -->

                  <v-expand-transition>
                    <div
                      v-if="profile.vehicle?.isOwner==2"
                      class="other-owner"
                    >
                      <v-text-field
                        v-if="fields.vehicle.rental.toggle"
                        v-model="fields.vehicle.rental.value"
                        :loading="fields.vehicle.rental.loading"
                        :disabled="fields.vehicle.rental.disabled"
                        :label="$t(fields.vehicle.rental.label)"
                        :rules="fields.vehicle.rental.rules"
                        :success="signup&&fields.vehicle.rental.success"
                        :error="fields.vehicle.rental.error"
                        outlined
                        color="secondary"
                        class="control"
                        @focus="onFormFocus(true)"
                        @blur="onFormFocus(false)"
                        @input="profileChanged('vehicle.rental')"
                      >
                        <template v-slot:message="data">
                          {{ $t(data.message) }}
                        </template>
                      </v-text-field>
                    </div>
                    <div
                      v-else-if="profile.vehicle?.isOwner==0"
                      class="other-owner"
                    >
                      <v-text-field
                        v-if="fields.vehicle.ownerName.toggle"
                        v-model="fields.vehicle.ownerName.value"
                        :loading="fields.vehicle.ownerName.loading"
                        :disabled="fields.vehicle.ownerName.disabled"
                        :label="$t(fields.vehicle.ownerName.label)"
                        :rules="fields.vehicle.ownerName.rules"
                        :success="signup&&fields.vehicle.ownerName.success"
                        :error="fields.vehicle.ownerName.error"
                        outlined
                        color="secondary"
                        class="control"
                        @focus="onFormFocus(true)"
                        @blur="onFormFocus(false)"
                        @input="profileChanged('vehicle.ownerName')"
                      >
                        <template v-slot:message="data">
                          {{ $t(data.message) }}
                        </template>
                      </v-text-field>
                      <v-text-field
                        v-if="fields.vehicle.ownerEmail.toggle"
                        v-model="fields.vehicle.ownerEmail.value"
                        :loading="fields.vehicle.ownerEmail.loading"
                        :disabled="fields.vehicle.ownerEmail.disabled"
                        :label="$t(fields.vehicle.ownerEmail.label)"
                        :hint="fields.vehicle.ownerEmail.hint"
                        :rules="fields.vehicle.ownerEmail.rules"
                        :success="signup&&fields.vehicle.ownerEmail.success"
                        :error="fields.vehicle.ownerEmail.error"
                        outlined
                        color="secondary"
                        class="control"
                        @focus="onFormFocus(true)"
                        @blur="onFormFocus(false)"
                        @input="profileChanged('vehicle.ownerEmail')"
                      >
                        <template v-slot:message="data">
                          {{ $t(data.message) }}
                        </template>
                      </v-text-field>
                      <v-text-field
                        v-if="fields.vehicle.ownerRg.toggle"
                        v-model="fields.vehicle.ownerRg.value"
                        :loading="fields.vehicle.ownerRg.loading"
                        :disabled="fields.vehicle.ownerRg.disabled"
                        :label="$t(fields.vehicle.ownerRg.label)"
                        :rules="fields.vehicle.ownerRg.rules"
                        :success="signup&&fields.vehicle.ownerRg.success"
                        :error="fields.vehicle.ownerRg.error"
                        outlined
                        color="secondary"
                        class="control"
                        @focus="onFormFocus(true)"
                        @blur="onFormFocus(false)"
                        @input="profileChanged('vehicle.ownerRg')"
                      >
                        <template v-slot:message="data">
                          {{ $t(data.message) }}
                        </template>
                      </v-text-field>
                      <v-text-field
                        v-if="fields.vehicle.ownerOrg_exp.toggle"
                        v-model="fields.vehicle.ownerOrg_exp.value"
                        :loading="fields.vehicle.ownerOrg_exp.loading"
                        :disabled="fields.vehicle.ownerOrg_exp.disabled"
                        :label="$t(fields.vehicle.ownerOrg_exp.label)"
                        :rules="fields.vehicle.ownerOrg_exp.rules"
                        :success="signup&&fields.vehicle.ownerOrg_exp.success"
                        :error="fields.vehicle.ownerOrg_exp.error"
                        outlined
                        color="secondary"
                        class="control"
                        @focus="onFormFocus(true)"
                        @blur="onFormFocus(false)"
                        @input="profileChanged('vehicle.ownerOrg_exp')"
                      >
                        <template v-slot:message="data">
                          {{ $t(data.message) }}
                        </template>
                      </v-text-field>
                      <v-text-field
                        v-if="fields.vehicle.ownerCpf.toggle"
                        v-model="fields.vehicle.ownerCpf.value"
                        v-mask="fields.vehicle.ownerCpf.mask"
                        :loading="fields.vehicle.ownerCpf.loading"
                        :disabled="fields.vehicle.ownerCpf.disabled"
                        :label="$t(fields.vehicle.ownerCpf.label)"
                        :rules="fields.vehicle.ownerCpf.rules"
                        :success="signup&&fields.vehicle.ownerCpf.success"
                        :error="fields.vehicle.ownerCpf.error"
                        outlined
                        color="secondary"
                        class="control"
                        @focus="onFormFocus(true)"
                        @blur="onFormFocus(false)"
                        @input="profileChanged('vehicle.ownerCpf')"
                      >
                        <template v-slot:message="data">
                          {{ $t(data.message) }}
                        </template>
                      </v-text-field>
                      <v-text-field
                        v-if="fields.vehicle.ownerRelative.toggle"
                        v-model="fields.vehicle.ownerRelative.value"
                        :loading="fields.vehicle.ownerRelative.loading"
                        :disabled="fields.vehicle.ownerRelative.disabled"
                        :label="$t(fields.vehicle.ownerRelative.label)"
                        :rules="fields.vehicle.ownerRelative.rules"
                        :success="signup&&fields.vehicle.ownerRelative.success"
                        :error="fields.vehicle.ownerRelative.error"
                        outlined
                        color="secondary"
                        class="control"
                        @focus="onFormFocus(true)"
                        @blur="onFormFocus(false)"
                        @input="profileChanged('vehicle.ownerRelative')"
                      >
                        <template v-slot:message="data">
                          {{ $t(data.message) }}
                        </template>
                      </v-text-field>
                    </div>
                  </v-expand-transition>
                </div>
              </div>
            </v-list-group>

            <v-divider v-if="sectionState.job?.toggle" />
            <v-list-group 
              v-if="sectionState.job?.toggle"
              v-model="sections.job"
              ref="job"
              :disabled="signup ? false : !recentAuth"
              :ripple="false"
              :class="[!sectionState.job?.completed&&sectionState.job?.required ? 'pending' : 'completed']"
              class="form-group"
              @click.native="toggleAuth(true, 'job'); scrollToSection('job')"
            >
              <template v-slot:appendIcon>
                <v-icon v-show="ready" class="section-icon">
                  {{ sectionState.job?.icon }}
                </v-icon>
              </template>
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('profile.job.title') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('profile.job.subtitle') }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <div 
                class="form-section"
              >
                <div v-if="ready" class="section-content pa-4 pb-0">
                  <m-select
                    v-if="fields.job.products.toggle"
                    :value="fields.job.products.value"
                    :options="fields.job.products.items"
                    :loading="fields.job.products.loading"
                    :disabled="fields.job.products.disabled"
                    :label="$t(fields.job.products.label)"
                    :hint="fields.job.products.hint"
                    :rules="fields.job.products.rules"
                    :success="signup&&fields.job.products.success"
                    :error="fields.job.special_projects.error"
                    dark
                    multiple
                    outlined
                    vertical
                    color="secondary"
                    class="control"
                    @change="(value) => setField('job.products', value)"
                  />
                  <v-select
                    v-if="fields.job.special_projects.toggle"
                    v-model="fields.job.special_projects.value"
                    :items="translate(fields.job.special_projects.items)"
                    :loading="fields.job.special_projects.loading"
                    :disabled="fields.job.special_projects.disabled"
                    :label="$t(fields.job.special_projects.label)"
                    :placeholder="$t(fields.job.special_projects.placeholder)"
                    :rules="fields.job.special_projects.rules"
                    :success="signup&&fields.job.special_projects.success"
                    :error="fields.job.special_projects.error"
                    :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                    :append-icon="icons.mdiHelpCircleOutline"
                    outlined
                    color="secondary"
                    class="control fixed-icon"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @click:append.stop="toggleHelp"
                    @change="profileChanged('job.special_projects')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-select>
                  <!-- <a class="hint text-caption" @click="toggleHelp">
                    {{ fields.job.special_projects.hint }}
                  </a> -->
                  <v-select
                    v-if="fields.job.service_model.toggle"
                    v-model="fields.job.service_model.value"
                    :items="translate(fields.job.service_model.items, ['text', 'caption'])"
                    :loading="fields.job.service_model.loading"
                    :disabled="fields.job.service_model.disabled"
                    :label="$t(fields.job.service_model.label)"
                    :placeholder="$t(fields.job.service_model.placeholder)"
                    :hint="fields.job.service_model.hint"
                    :rules="fields.job.service_model.rules"
                    :success="signup&&fields.job.service_model.success"
                    :error="fields.job.service_model.error"
                    :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @change="profileChanged('job.service_model')"
                  >
                    <template v-slot:item="data">
                      <v-list-item 
                        v-bind="data.attrs"
                        v-on="data.on"
                      >
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ data.item.text }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ data.item.caption }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>

                  <v-select
                    v-if="fields.job.fulltime.toggle"
                    v-model="fields.job.fulltime.value"
                    :items="translate(fields.job.fulltime.items)"
                    :loading="fields.job.fulltime.loading"
                    :disabled="fields.job.fulltime.disabled"
                    :label="$t(fields.job.fulltime.label)"
                    :placeholder="$t(fields.job.fulltime.placeholder)"
                    :hint="fields.job.fulltime.hint"
                    :rules="fields.job.fulltime.rules"
                    :success="signup&&fields.job.fulltime.success"
                    :error="fields.job.fulltime.error"
                    persistent-hint
                    :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @change="profileChanged('job.fulltime')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-select>

                  <m-select
                    v-if="fields.job.apps.toggle"
                    :value="fields.job.apps.value"
                    :options="translate(fields.job.apps.items)"
                    :loading="fields.job.apps.loading"
                    :disabled="fields.job.apps.disabled"
                    :label="$t(fields.job.apps.label)"
                    :hint="fields.job.apps.hint"
                    :rules="fields.job.apps.rules"
                    :success="signup&&fields.job.apps.success"
                    :error="fields.job.apps.error"
                    dark
                    multiple
                    outlined
                    vertical
                    color="secondary"
                    class="control"
                    @change="(value) => setField('job.apps', value)"
                  />
                  <v-expand-transition>
                    <m-field
                      v-if="fields.job.apps_percent.toggle&&!!fields.job.apps.value&&fields.job.apps.value.length>1"
                      :value="fields.job.apps_percent.value"
                      :loading="fields.job.apps_percent.loading"
                      :disabled="fields.job.apps_percent.disabled"
                      :label="$t(fields.job.apps_percent.label)"
                      :hint="fields.job.apps_percent.hint"
                      :success="signup&&fields.job.apps_percent.success"
                      :error="fields.job.apps_percent.error"
                      dark
                      color="secondary"
                      class="apps-percent control"
                    >
                      <div
                        v-for="selected in fields.job.apps_percent.value"
                        :key="'app-percent-'+selected.app"
                        class="app-percent d-flex align-center pl-4"
                      >
                        <span class="white--text text-body-1 mr-2">
                          {{ appLabel(selected) }}:
                        </span>
                        <!-- <v-spacer /> -->
                        <v-select
                          v-model="selected.value"
                          :items="fields.job.apps_percent.items"
                          :placeholder="$t(fields.job.apps_percent.placeholder)"
                          :success="signup&&fields.job.apps_percent.success"
                          :error="fields.job.apps_percent.error"
                          :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                          solo
                          flat
                          hide-details
                          color="secondary"
                          class="transparent"
                          @change="updateAppsPercent(true, selected)"
                        >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-select>
                      </div>
                    </m-field>
                  </v-expand-transition>

                  <v-select
                    v-if="fields.job.journey.toggle"
                    v-model="fields.job.journey.value"
                    :items="translate(fields.job.journey.items)"
                    :loading="fields.job.journey.loading"
                    :disabled="fields.job.journey.disabled"
                    :label="$t(fields.job.journey.label)"
                    :placeholder="$t(fields.job.journey.placeholder)"
                    :hint="fields.job.journey.hint"
                    persistent-hint
                    :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                    :rules="fields.job.journey.rules"
                    :success="signup&&fields.job.journey.success"
                    :error="fields.job.journey.error"
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @change="profileChanged('job.journey')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-select>

                  <v-autocomplete
                    v-if="fields.job.region.toggle"
                    v-model="fields.job.region.value"
                    :items="fields.job.region.items"
                    :filter="fields.job.region.filter"
                    :loading="fields.job.region.loading"
                    :disabled="fields.job.region.disabled"
                    :label="$t(fields.job.region.label)"
                    :hint="fields.job.region.hint"
                    :rules="fields.job.region.rules"
                    :success="signup&&fields.job.region.success"
                    :error="fields.job.region.error"
                    :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                    item-text="title"
                    item-value="title"
                    hide-no-data
                    outlined
                    color="secondary"
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @change="profileChanged('job.region')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-autocomplete>

                  <v-text-field
                    v-if="fields.job.rating.toggle"
                    v-model="fields.job.rating.value"
                    v-mask="fields.job.rating.mask"
                    :loading="fields.job.rating.loading"
                    :disabled="fields.job.rating.disabled"
                    :label="$t(fields.job.rating.label)"
                    :hint="fields.job.rating.hint"
                    :rules="fields.job.rating.rules"
                    :success="signup&&fields.job.rating.success"
                    :error="fields.job.rating.error"
                    type="tel"
                    color="secondary"
                    outlined
                    class="control"
                    @focus="onFormFocus(true)"
                    @blur="onFormFocus(false)"
                    @input="profileChanged('job.rating')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>
                  
                  <v-subheader 
                    v-if="fields.job.experience.toggle"
                    class="px-0 mt-n4 caption"
                  >
                    {{ $t(fields.job.experience.label) }}
                  </v-subheader>
                  <v-slider
                    v-if="fields.job.experience.toggle"
                    v-model="fields.job.experience.value"
                    :disabled="fields.job.experience.disabled"
                    :hint="fields.job.experience.hint"
                    :success="signup&&fields.job.experience.success"
                    :error="fields.job.experience.error"
                    thumb-label="always"
                    :max="fields.job.experience.max"
                    min="0"
                    :color="fields.job.experience.error ? 'warning' : 'secondary'"
                    outlined
                    class="control mb-4"
                    @input="profileChanged('job.experience'); roundExperience();"
                  >
                    <template v-slot:thumb-label="{ value }">
                      <v-progress-circular 
                        v-if="fields.job.experience.loading"
                        size="16"
                        width="2"
                        indeterminate
                        color="white"
                      />
                      <v-icon 
                        v-else
                        small
                      >
                        {{ icons.mdiArrowLeftRight }}
                      </v-icon>
                    </template>
                    <template v-slot:prepend>
                      <span
                        class="body-1"
                        style="min-width: 80px; white-space: nowrap;"
                      >
                        {{ fields.job.experience.value ? $moment.duration(fields.job.experience.value, 'months').humanize() : $t('general.select')+':' }}
                      </span>
                    </template>
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-slider>
                </div>
              </div>
            </v-list-group>

            <v-divider v-if="sectionState.payment?.toggle" />
            <v-list-group 
              v-if="sectionState.payment?.toggle"
              v-model="sections.payment"
              ref="payment"
              :disabled="signup ? false : !recentAuth"
              :ripple="false"
              :class="[!sectionState.payment?.completed&&sectionState.payment?.required ? 'pending' : 'completed']"
              class="form-group"
              @click.native="toggleAuth(true, 'payment'); scrollToSection('payment')"
            >
              <template v-slot:appendIcon>
                <v-icon class="section-icon">
                  {{ sectionState.payment?.icon }}
                </v-icon>
              </template>
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('profile.payment.title') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('profile.payment.subtitle') }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <div 
                class="form-section"
              >
                <div v-if="ready" class="section-content pa-4 pb-0">
                  <v-text-field
                    v-if="fields.payment.pix.toggle"
                    v-model="fields.payment.pix.value"
                    :loading="fields.payment.pix.loading"
                    :disabled="fields.payment.pix.disabled"
                    :label="$t(fields.payment.pix.label)"
                    :hint="fields.payment.pix.hint"
                    :rules="fields.payment.pix.rules"
                    :success="signup&&fields.payment.pix.success"
                    :error="fields.payment.pix.error"
                    outlined
                    color="secondary"
                    class="control"
                    @keyup="profileChanged('payment.pix')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-autocomplete
                    v-if="fields.payment.bank.toggle"
                    v-model="fields.payment.bank.value"
                    :items="bankList"
                    :loading="fields.payment.bank.loading"
                    :disabled="fields.payment.bank.disabled"
                    :label="$t(fields.payment.bank.label)"
                    :hint="fields.payment.bank.hint"
                    :rules="fields.payment.bank.rules"
                    :success="signup&&fields.payment.bank.success"
                    :error="fields.payment.bank.error"
                    :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                    max-height="64vw"
                    item-text="title"
                    item-value="id"
                    searchable
                    dark
                    outlined
                    vertical
                    color="secondary"
                    @change="profileChanged('payment.bank')"
                    class="control"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-autocomplete>

                  <v-text-field
                    v-if="fields.payment.branch.toggle"
                    v-model="fields.payment.branch.value"
                    :loading="fields.payment.branch.loading"
                    :disabled="fields.payment.branch.disabled"
                    :label="$t(fields.payment.branch.label)"
                    :hint="fields.payment.branch.hint"
                    :rules="fields.payment.branch.rules"
                    :success="signup&&fields.payment.branch.success"
                    :error="fields.payment.branch.error"
                    outlined
                    color="secondary"
                    class="control"
                    @keyup="profileChanged('payment.branch')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>

                  <v-row 
                    v-if="fields.payment.account.toggle"
                    class="my-0"
                  >
                    <v-col cols="7" class="py-0">
                      <v-text-field
                        v-model="fields.payment.account.value"
                        :loading="fields.payment.account.loading"
                        :disabled="fields.payment.account.disabled"
                        :label="$t(fields.payment.account.label)"
                        :hint="fields.payment.account.hint"
                        :rules="fields.payment.account.rules"
                        :success="signup&&fields.payment.account.success"
                        :error="fields.payment.account.error"
                        hide-details="auto"
                        outlined
                        color="secondary"
                        class="control"
                        @keyup="profileChanged('payment.account')"
                      >
                        <template v-slot:message="data">
                          {{ $t(data.message) }}
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col class="py-0">
                      <v-text-field
                        v-model="fields.payment.digit.value"
                        :loading="fields.payment.digit.loading"
                        :disabled="fields.payment.digit.disabled"
                        :label="$t(fields.payment.digit.label)"
                        :hint="fields.payment.digit.hint"
                        :rules="fields.payment.digit.rules"
                        :success="signup&&fields.payment.digit.success"
                        :error="fields.payment.digit.error"
                        hide-details="auto"
                        outlined
                        color="secondary"
                        class="control"
                        @keyup="profileChanged('payment.digit')"
                      >
                        <template v-slot:message="data">
                          {{ $t(data.message) }}
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <div 
                    class="account-owner my-4"
                  >
                    <v-checkbox
                      v-model="fields.payment.owner"
                      color="secondary"
                      class="control"
                      @change="changePaymentOwner()"
                    >
                      <template v-slot:label>
                        <span class="text-body-1 font-weight-medium">{{ $t(fields.payment.owner ? 'profile.payment.my_account' : 'profile.payment.third_party_account') }}</span>
                      </template>
                    </v-checkbox>
                    <v-text-field
                      v-if="fields.payment.name.toggle"
                      v-model="fields.payment.name.value"
                      :loading="fields.payment.name.loading"
                      :label="$t(fields.payment.name.label)"
                      :hint="fields.payment.name.hint"
                      :success="signup&&fields.payment.name.success"
                      :error="fields.payment.name.error"
                      outlined
                      color="secondary"
                      class="control"
                      @keyup="profileChanged('payment.name')"
                    >
                      <template v-slot:message="data">
                        {{ $t(data.message) }}
                      </template>
                    </v-text-field>

                    <v-text-field
                      v-if="fields.payment.cpf.toggle"
                      v-model="fields.payment.cpf.value"
                      v-mask="fields.cpf.mask"
                      :loading="fields.payment.cpf.loading"
                      :label="$t(fields.payment.cpf.label)"
                      :hint="fields.payment.cpf.hint"
                      :success="signup&&fields.payment.cpf.success"
                      :error="fields.payment.cpf.error"
                      outlined
                      color="secondary"
                      class="control"
                      @keyup="profileChanged('payment.cpf')"
                    >
                      <template v-slot:message="data">
                        {{ $t(data.message) }}
                      </template>
                    </v-text-field>
                  </div>

                  <v-select
                    v-if="fields.payment.type.toggle"
                    v-model="fields.payment.type.value"
                    :items="translate(fields.payment.type.items)"
                    :loading="fields.payment.type.loading"
                    :disabled="fields.payment.type.disabled"
                    :label="$t(fields.payment.type.label)"
                    :hint="fields.payment.type.hint"
                    :rules="fields.payment.type.rules"
                    :success="signup&&fields.payment.type.success"
                    :error="fields.payment.type.error"
                    :menu-props="{ bottom: true, offsetY: true, 'content-class': 'profile-menu secondary' }"
                    outlined
                    color="secondary"
                    class="control"
                    @change="profileChanged('payment.type')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-select>

                  <v-text-field
                    v-if="fields.payment.compl.toggle"
                    v-model="fields.payment.compl.value"
                    :loading="fields.payment.compl.loading"
                    :disabled="fields.payment.compl.disabled"
                    :label="$t(fields.payment.compl.label)"
                    :hint="fields.payment.compl.hint"
                    :rules="fields.payment.compl.rules"
                    :success="signup&&fields.payment.compl.success"
                    :error="fields.payment.compl.error"
                    outlined
                    color="secondary"
                    class="control"
                    @keyup="profileChanged('payment.compl')"
                  >
                    <template v-slot:message="data">
                      {{ $t(data.message) }}
                    </template>
                  </v-text-field>
                </div>
              </div>
            </v-list-group>

            <v-divider />
            <v-list-group 
              ref="account"
              :ripple="false"
              class="form-group"
              @click="scrollToSection('account')"
            >
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('profile.account.title') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('profile.account.subtitle') }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
              
              <div class="form-section pa-0 pb-4">
                <v-list-item
                  v-if="signup&&installed"
                  color="secondary"
                >
                  <v-list-item-action>
                    <v-checkbox 
                      v-model="profile.gps"
                      :loading="fields.gps.loading"
                      :readonly="fields.gps.disabled"
                      color="primary"
                      class="control"
                      @click="requestGpsPermission()"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t(fields.gps.label) }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item
                  v-if="installed"
                  color="secondary"
                >
                  <v-list-item-action>
                    <v-checkbox 
                      v-model="profile.push"
                      :loading="fields.push.loading"
                      :readonly="fields.push.disabled"
                      color="primary"
                      class="control"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t(fields.push.label) }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-menu
                  offset-y
                  bottom
                  :disabled="!!ghost || localeLoading"
                  max-height="280"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-list-item
                      color="secondary"
                      :disabled="!!ghost"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-list-item-action>
                        <span class="text-h6">{{ selectedLocale.emoji }}</span>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('profile.account.locale') }}: {{ selectedLocale.text }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <v-list
                    class="profile-menu secondary"
                  >
                    <v-list-item
                      v-for="option in localeOptions"
                      :key="option.value"
                      :class="{ 'secondary--text': option.value === $i18n.locale }"
                      :disabled="localeLoading"
                      @click="onLocaleChange(option.value)"
                    >
                      <v-list-item class="text-body-1">
                        <span class="mr-6">{{ option.emoji }}</span>
                        <span>{{ option.full }}</span>
                      </v-list-item>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-divider inset />
                
                <v-list-item
                  color="secondary"
                  @click="password.toggle = true;"
                >
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                    >
                      <v-icon>{{ icons.mdiPencilLock }}</v-icon>
                    </v-btn>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t('profile.password.change') }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item
                  color="secondary"
                  @click="logout(ghost); logoutBtn.loading = true;"
                >
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      :loading="logoutBtn.loading"
                    >
                      <v-icon>{{ icons.mdiClose }}</v-icon>
                    </v-btn>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t('general.logout') }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-divider inset />

                <v-list-item
                  v-if="signup"
                  @click="dialogs.deleteAccount.toggle = true;"
                >
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      class="error--text text--darken-2"
                    >
                      <v-icon>{{ icons.mdiDelete }}</v-icon>
                    </v-btn>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title class="error--text text--darken-2">
                      {{ $t('profile.delete_account.button') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                
                <!-- <v-btn 
                  v-if="!recentAuth && !signup"
                  text 
                  outlined
                  block
                  large
                  class="my-8"
                  @click="dialogs.auth.toggle = true;"
                >
                  Alterar meus dados
                </v-btn> 

                <v-btn 
                  text 
                  class="my-8"
                  @click="password.toggle = true;"
                >
                  Alterar minha senha
                </v-btn>
                
                <v-btn 
                  text 
                  class="my-4"
                  @click="logout()"
                >
                  Logout
                </v-btn>  
                -->
              </div>
            </v-list-group>

            <!-- <v-divider /> -->
          </v-list>
        </v-form>
        
      </v-card-text>
      <v-fab-transition>
        <v-btn
          v-show="saveable&&!signup"
          color="primary darken-1"
          class="btn-save black--text"
          dark
          absolute
          bottom
          right
          fab
          :loading="saveBtn.loading"
          :disabled="saveBtn.disabled"
          @click="updateProfile"
        >
          <v-icon>{{ icons.mdiCheck }}</v-icon>
        </v-btn>
      </v-fab-transition>
    </v-card>

    <v-scroll-y-transition>
      <v-sheet
        v-if="user.status=='ACC'&&!express&&(!keyboard||completion.percent==1)"
        color="transparent"
        elevation="0"
        class="profile-footer d-flex flex-column"
        :class="{ 'no-events': completion.percent!=1 }"
      >
        <v-btn 
          v-if="completion.percent==1"
          large
          color="accent"
          class="btn-cta grey--text text--darken-4 ma-4"
          :loading="sendBtn.loading||requestEvaluationLoading"
          @click="requestEvaluation()"
        >
          {{ $t('profile.send') }}
        </v-btn>
        <completion-progress
          v-else-if="completion.percent!=null"
          :completion="parseInt(completion.percent*100)"
          class="profile-completion pa-8"
        />
      </v-sheet>
    </v-scroll-y-transition>

    <v-dialog
      v-model="dialogs.deleteAccount.toggle"
      overlay-opacity=".8"
      transition="slide-y-reverse-transition"
    >
      <v-card color="black">
        <v-card-title>{{ $t('profile.delete_account.title') }}</v-card-title>
        <v-card-text class="py-4">
          <p 
            class="body-2"
            v-html="formatText($t('profile.delete_account.text', { keyword: $t('profile.delete_account.keyword') }))"
          />
          <p 
            class="body-2 mb-8"
            v-html="formatText($t('profile.delete_account.warning'))"
          />

          <v-text-field 
            v-model="dialogs.deleteAccount.input" 
            :label="$t('profile.delete_account.keyword')" 
            :disabled="dialogs.deleteAccount.loading"
            hide-details
            outlined
            color="error"
          />
        </v-card-text>

        <v-card-actions class="pa-6">
          <v-btn
            color="grey"
            text
            @click="dialogs.deleteAccount.toggle = false;"
          >
            {{ $t('general.back') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="error"
            :disabled="dialogs.deleteAccount.input!=$t('profile.delete_account.keyword')||dialogs.deleteAccount.loading"
            :loading="dialogs.deleteAccount.loading"
            @click="deleteAccount"
          >
            {{ $t('general.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="dialogs.auth.toggle"
      overlay-opacity=".8"
      transition="slide-y-reverse-transition"
    >
      <v-card color="black">
        <v-card-title>{{ $t('profile.password.type_in') }}</v-card-title>
        <v-card-text class="py-4">
          <p class="text-body-2">
            {{ $t('profile.password.text') }}
          </p>

          <v-text-field 
            v-model="dialogs.auth.input" 
            :label="$t(dialogs.auth.label)" 
            :disabled="dialogs.auth.disabled"
            :loading="dialogs.auth.loading"
            :hint="dialogs.auth.hint"
            :append-icon="dialogs.auth.visible ? icons.mdiEye : icons.mdiEyeOff"
            :type="dialogs.auth.visible ? 'text' : 'password'"
            hide-details="auto"
            outlined
            color="primary"
            class="my-2"
            @click:append="dialogs.auth.visible = !dialogs.auth.visible"
            @keyup.enter="getAuth(user.username, dialogs.auth.input)"
          />
          <v-btn
            color="secondary"
            text
            small
            :disabled="dialogs.passwordRecovery.loading||dialogs.passwordRecovery.loading"
            :loading="dialogs.passwordRecovery.loading"
            @click="togglePasswordRecovery(true)"
          >
            {{ $t('profile.password.forgot') }}
          </v-btn>
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="grey"
            text
            @click="dialogs.auth.toggle = false;"
          >
            {{ $t('general.back') }}  
          </v-btn>
          <v-spacer />
          <v-btn
            color="secondary"
            text
            :disabled="dialogs.auth.input==''||dialogs.auth.input==null||dialogs.auth.loading"
            :loading="dialogs.auth.loading"
            @click="getAuth(user.username, dialogs.auth.input)"
          >
            {{ $t('profile.password.confirm') }}  
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="password.toggle"
      overlay-opacity=".8"
      transition="slide-y-reverse-transition"
    >
      <v-card color="black">
        <v-card-title>{{ $t('profile.password.change') }}</v-card-title>
        <v-card-text class="py-4">
          <p class="body-1">
            {{ $t('profile.password.change_text') }}
          </p>

          <v-text-field 
            v-model="password.input" 
            :label="$t(password.label)" 
            :disabled="password.disabled"
            :loading="password.loading"
            :hint="$t(password.hint)"
            :append-icon="password.visible ? icons.mdiEye : icons.mdiEyeOff"
            :type="password.visible ? 'text' : 'password'"
            outlined
            @click:append="password.visible = !password.visible"
            @keyup.enter="updatePassword"
            @change="validatePassword"
            @focus="validatePassword"
            @keyup="validatePassword"
            color="primary"
          />
          <v-text-field 
            v-model="confirmPassword.input" 
            :label="$t(confirmPassword.label)" 
            :disabled="confirmPassword.disabled"
            :loading="confirmPassword.loading"
            :hint="$t(confirmPassword.error)"
            :append-icon="password.visible ? icons.mdiEye : icons.mdiEyeOff"
            :type="password.visible ? 'text' : 'password'"
            outlined
            @click:append="password.visible = !password.visible"
            @keyup.enter="updatePassword"
            @change="validatePassword"
            @focus="validatePassword"
            @keyup="validatePassword"
            color="primary"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey"
            text
            @click="password.toggle = false;"
          >
            {{ $t('general.back') }}
          </v-btn>
          <v-btn
            color="secondary"
            text
            :disabled="passwordBtn.disabled"
            @click="updatePassword()"
          >
            {{ $t('general.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <forgot-password 
      :reset="false" 
      @validated="authUpdated"
    />
  </v-dialog>
</template>

<style lang="scss">

  .v-dialog--fullscreen > .v-card:not(.v-sheet--tile):not(.v-card--shaped) {
    border-radius: 0;
  }

  #profile .v-toolbar {
    background: var(--v-background-base);
    z-index: 2;
  }

  #profile .error {
    color: var(--v-warning-base) !important;
  }

  #profile .content {
    padding: 16px;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }

  #profile .control {
    margin: 4px 0 12px !important;
  }
  #profile .control ~ .hint {
    display: block;
    margin: -24px 0 24px;
    padding: 0 12px;
  }
  .profile-menu.secondary {
    border: 1px solid var(--v-secondary-base) !important;
    background: var(--v-background-base) !important;
  }
  .profile-menu.secondary .v-list-item--active {
    color: var(--v-secondary-base) !important;
  }


  hr {
    margin: 8px 0;
  }

  .profile-header .title {
    line-height: 1.25;
  }

  .profile-form .overline {
    margin-top: 16px;
  }

  .profile-img {
    position: relative;
  }

  #profile .profile-img .avatar {
    overflow: initial;
    border: 4px solid color-mix(in srgb, var(--v-secondary-base) 64%, transparent) !important;
    .photo {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .profile-img .btn-cancel, .profile-img .btn-ok {
      bottom: 2rem !important;
  }
  .profile-img .btn-cancel {
    left: 2rem;
  }
  .profile-img .btn-ok {
    right: 2rem;
  }

  .profile-form .form-group.completed .section-icon, .profile-form .form-group.pending .section-icon, .profile-form .fixed-icon .v-icon {
    transform: rotate(0) !important;
  }
  .profile-form .form-group.pending .section-icon {
    color: var(--v-warning-base);
  }
  #app .profile-form .error--text {
    color: var(--v-warning-base) !important;
    caret-color: var(--v-warning-base) !important;
  }

  .croppa-container {
    width: 240px;
    height: 240px;
    background: var(--v-background-base);
    border-radius: 50%;
    overflow: hidden;
  }

  .profile-footer {
    position: fixed;
    bottom: 0;
    padding-bottom: var(--safe-area-bottom);
    left: 0;
    right: 0;
    background: rgb(38,39,44);
    background: linear-gradient(0deg, var(--v-background-base) 64%, rgba(0,0,0,0) 100%);
  }
  .profile-footer.no-events {
    pointer-events: none;
  }


  .profile-header .btn-upload {
    bottom: 0 !important;
    right: -1.5rem;
  }

  #profile .btn-save {
    bottom: 1rem !important;
  }

  #app #change-password.v-dialog:not(.v-dialog--fullscreen) {
    max-height: initial;
  }

</style>

<script>
  // Icons
  import {
    mdiHelpCircleOutline,
    mdiEye,
    mdiEyeOff,
    mdiClose,
    mdiCheck,
    mdiPencil,
    mdiPencilLock,
    mdiCheckDecagram,
    mdiLock,
    mdiArrowLeftRight,
    mdiDelete,
    mdiAlertCircleOutline
  } from '@mdi/js'

  // Utilities
  import services from '@/services.js'
  import dictionary from '@/dictionary.js'
  import { fields, completion, parser } from '@/services/profile.js'
  import { steps as _steps } from '@/services/signup'
  import { sync, get } from 'vuex-pathify'
  import { mask } from 'vue-the-mask'
  import { Scroll } from 'vuetify/lib/directives';
  import _ from 'lodash';
  import axios from 'axios';
  import device from '@/device.js';
  const md5 = require('md5');
  import { App } from '@capacitor/app';
  import { Capacitor } from '@capacitor/core';
  import cities from '@/assets/data/cidades.json'


  export default {
    name: 'Profile',

    props: {
      helpContent: {
        type: Object,
        default: () => ({}),
      },
      toggle: {
        type: Boolean,
        default: false,
      },
      superStatus: {
        type: String,
        default: 'ACT',
      }
    },

    components: {
      IconBase: () => import('@/components/IconBase'),
      AvatarDefault: () => import('@/components/icons/AvatarDefault'),
      CompletionProgress: () => import('@/components/CompletionProgress'),
      ForgotPassword: () => import('@/views/login/ForgotPassword'),
      MSelect: () => import('@/components/mSelect'),
      MField: () => import('@/components/mField'),
      Badges: () => import('@/components/badges/BadgesList'),
    },

    data () {
      return {
        api: process.env.VUE_APP_ROOT_API,
        icons: {
          mdiHelpCircleOutline,
          mdiEye,
          mdiEyeOff,
          mdiClose,
          mdiCheck,
          mdiPencil,
          mdiPencilLock,
          mdiCheckDecagram,
          mdiLock,
          mdiArrowLeftRight,
          mdiDelete,
          mdiAlertCircleOutline
        },
        offsetTop: 0,
        toggleUpload: false,
        cropper: {
        },
        saveBtn: {
          disabled: false,
          loading: false
        },
        logoutBtn: {
          loading: false
        },
        club_id: {
          toggle: false,
        },
        badge_view: {
          mini: true
        },
        keyboard: false,
        dialogs: {
          auth: {
            toggle: false,
            input: '',
            visible: false,
            loading: false,
            label: 'profile.password.label',
            section: 'personal'
          },
          deleteAccount: {
            toggle: false,
            loading: false,
            disabled: true,
            input: null,
          },
          passwordRecovery: {
            toggle: false,
            loading: false,
            disabled: true,
            input: null,
          },
        },
        password: {
          toggle: false,
          input: '',
          visible: false,
          loading: false,
          disabled: false,
          label: 'profile.password.new',
          hint: 'profile.password.new_hint',
        },
        confirmPassword: {
          input: '',
          visible: false,
          loading: false,
          disabled: false,
          label: 'profile.password.new_confirm',
          hint: 'profile.password.new_confirm_hint',
        },
        passwordBtn: {
          disabled: false,
          loading: false
        },
        sendBtn: {
          disabled: false,
          loading: false
        },
        timer: {
          controller: null,
          count: 0,
        },
        deviceInfo: null,
        os: null,
        app_version: APP_VERSION,
        app_build: null,
        changed: [],
        localeLoading: false,
        sections: {
          personal: false,
          job: false,
          vehicle: false,
          payment: false,
        },
        fields,
        bankKeyword: '',
        dictionary: { ...dictionary.profile },
        loaded: false,
        setup: false,
        ghost: null,
        installed: Capacitor.isNativePlatform(),
      };
    },

    computed: {
      views: sync('app/views'),
      view: sync('app/views@profile'),
      username: get('user/username'),
      status: sync('user/status'),
      statusChange: sync('user/statusChange'),
      vehicles: get('app/views@help.vehicles'),
      bankList: sync('user/bankList'),
      profile: sync('user/profile'),
      express: sync('user/express'),
      checkedAddress: sync('user/profile@address.checkedAddress'),
      user: sync('user'),
      auth: sync('user/auth'),
      help: get('help/sections'),
      errors: sync('app/views@help.errors'),
      toast: sync('app/toast'),
      version: sync('app/version'),
      config: get('app/config'),
      countries: sync('app/countries'),

      toolbarElevation () {
        return this.offsetTop >= 16 ? 2 : 0;
      },

      localeOptions () {
        const meta = {
          'pt-br': { text: 'Português', full: 'Português (Brasil)', country: 'BR' },
          'es': { text: 'Español', full: 'Español', country: null },
          'es-ar': { text: 'Español', full: 'Español (Argentina)', country: 'AR' },
          'es-py': { text: 'Español', full: 'Español (Paraguay)', country: 'PY' },
        };
        const options = _(this.$i18n.availableLocales)
          .filter(code => String(code).includes('-'))
          .sortBy()
          .map(value => {
            const info = meta[value] || { text: value, full: value, country: null };
            const country = info.country
              ? _.find(this.countries, c => c.iso2 === info.country)
              : null;
            return {
              value,
              text: info.text,
              full: info.full,
              emoji: (country && country.emoji) || '🌐',
            };
          })
          .value();
        const current = this.$i18n.locale;
        if (current && !_.find(options, { value: current })) {
          const info = meta[current] || { text: current, full: current, country: null };
          const country = info.country
            ? _.find(this.countries, c => c.iso2 === info.country)
            : null;
          options.unshift({
            value: current,
            text: info.text,
            full: info.full,
            emoji: (country && country.emoji) || '🌐',
          });
        }
        return options;
      },

      selectedLocale () {
        return _.find(this.localeOptions, { value: this.$i18n.locale })
          || { text: this.$i18n.locale, full: this.$i18n.locale, emoji: '🌐' };
      },

      shortName () {
        const fullname = this.profile.fullname;
        const names = fullname != null ? fullname.split(' ') : ' ';
        return names[0] + ' ' + names[names.length-1].substr(0,1) + '.';
      },

      recentAuth () {
        return this.ghost!==null||this.$moment().diff(this.auth.timestamp, 'minutes')<=15;
      },

      signup () {
        return !['ACT', 'EXP'].includes(this.status);
      },

      mergedCompletion () {
        const config = this.config || {};
        // Get completion config for current org/fleet
        const completionConfig = this.getContent(this.profile, config.profile?.completion || {});
        // console.log('completionConfig', completionConfig);
        
        // Custom merge: merge default status with config status, but config sections completely replace default sections
        const mergeCompletion = (defaultObj, configObj) => {
          if (!configObj || _.isEmpty(configObj)) return defaultObj;
          
          const merged = _.cloneDeep(defaultObj);
          
          // For each status in config, merge with default status
          _.each(configObj, (configStatus, status) => {
            const defaultStatus = merged[status];
            // console.log('configStatus', configStatus, status, defaultStatus);
            // Skip if default value is a string reference (e.g. FDBX: 'FDB'); config cannot merge into it
            if (defaultStatus && typeof defaultStatus !== 'object' && !Array.isArray(defaultStatus)) return;
            // If status exists in default as an object, merge config sections into it
            if (defaultStatus && typeof defaultStatus === 'object') {
              // For each section in config, completely replace the default section
              _.each(configStatus, (configSection, sectionKey) => {
                const defaultSection = defaultStatus[sectionKey] || {};
                
                // Config section completely replaces default section when present
                // Use config values if defined, otherwise keep default values
                merged[status][sectionKey] = {
                  // Preserve default properties not in config (like percent)
                  percent: configSection.percent !== undefined ? configSection.percent : defaultSection.percent,
                  // Arrays are completely replaced by config if present
                  required: configSection.required !== undefined ? configSection.required : (defaultSection.required || []),
                  disabled: configSection.disabled !== undefined ? configSection.disabled : (defaultSection.disabled || []),
                  inherit: configSection.inherit !== undefined ? configSection.inherit : (defaultSection.inherit || []),
                };
              });
            } else {
              // Status doesn't exist in default, add it from config
              merged[status] = _.cloneDeep(configStatus);
            }
          });
          
          return merged;
        };
        
        return completionConfig=='default' ? completion : mergeCompletion(completion, completionConfig);
      },

      step () {
        const status = this.status;
        const express = this.express;
        const mergedCompletion = this.mergedCompletion;
        const steps = _steps;
        const resolveCompletion = (key) => {
          if (!key) return null;
          let config = mergedCompletion[key];
          while (_.isString(config)) config = mergedCompletion[config];
          return config;
        };
        const getSectionFields = (providerKey, sectionKey, visited = []) => {
          if (!providerKey || visited.includes(providerKey)) return { required: [], disabled: [] };
          visited = [...visited, providerKey];
          const config = resolveCompletion(providerKey);
          const section = config && config[sectionKey];
          if (!section) return { required: [], disabled: [] };
          let required = [...(section.required || [])];
          let disabled = [...(section.disabled || [])];
          if (section.inherit && Array.isArray(section.inherit)) {
            _.each(section.inherit, (provider) => {
              const inherited = getSectionFields(provider, sectionKey, visited);
              required = _.union(required, inherited.required);
              disabled = _.union(disabled, inherited.disabled);
            });
          }
          return { required, disabled };
        };
        let step = null;
        let sections = null;
        if (this.signup) {
          let stepKey = null;
          step = _.find(_.cloneDeep(steps), (step, key) => {
            const scope = _.isString(step.status) ? [step.status] : step.status;
            const current = !!scope && scope.includes(status) && (express===step.express||_.isNil(step.express));
            if (current) stepKey = key;
            return current;
          });
          if (stepKey) sections = resolveCompletion(stepKey);
        }else{
          step = {}
          sections = resolveCompletion(this.status);
        }
        if (!sections) {
          sections = resolveCompletion(status);
          if (!step && status === 'FDD' && steps.ENR) {
            step = _.cloneDeep(steps.ENR);
          }
        }
        
        // Deep clone sections to avoid mutating the original
        sections = _.cloneDeep(sections);
        
        // Process inheritance: merge required and disabled fields from inherited providers
        _.each(sections, (section, s) => {
          if (section.inherit && Array.isArray(section.inherit)) {
            _.each(section.inherit, provider => {
              const { required, disabled } = getSectionFields(provider, s);
              section.required = _.union(section.required || [], required);
              section.disabled = _.union(section.disabled || [], disabled);
            });
          }
          // Ensure arrays exist
          section.disabled = section.disabled || [];
          section.required = section.required || [];
        });
        
        return {
          ...step,
          sections,
          completion: step && _.has(step, 'completion') ? _.round(step.completion*100) : null
        };
      },

      completion () {
        const fields = this.fields;
        const step = this.step;
        const ownAccount = !!fields.payment?.owner;
        const sections = _.mapValues(step.sections, section => {
          const required = _.filter(section.required || [], (f) => {
            if (ownAccount && (f === 'payment.name' || f === 'payment.cpf')) return false;
            return true;
          });
          section.percent = _.isEmpty(required) ? 1 : _.filter(_.map(required, (f) => {
            const isValid = this.validateField(f);
            // validateField already sets the error state if the property exists
            // But ensure it's set even if property doesn't exist yet
            const field = _.get(this.fields, f);
            if (field) {
              _.set(this.fields, f + '.error', !isValid);
            }
            return isValid;
          }), (f) => f == true).length / required.length;
          return section;
        });
        const percent = _.meanBy(_.values(sections), section => section.percent);
        console.log('profile', _.round(percent*100, 2)+'%');
        return {
          sections,
          percent
        };
      },

      saveable () {
        const changed = this.changed;
        const completion = this.completion;
        return changed.length>0&&(completion.percent==1||!this.signup);
      },

      networkStatus () {
        return this.isOnline ? this.$t('general.online') : this.$t('errors.no_internet')
      },

      requestEvaluationLoading () {
        return this.completion.percent==100 && this.changed.length>0;
      },

      sectionState () {
        let state;
        const completion = this.completion;
        const sections = completion.sections;
        state = _.mapValues(sections, (section, s) => {
          const required = section.required;
          const completed = section.percent==1;
          const icon = this.sectionIcon(required, completed);
          const toggle = required.length > 0;
          return {
            required,
            completed,
            icon,
            toggle,
          }
        });
        return state;
      },

      vehicleList () {
        return _.uniq(_.map(this.vehicles, 'model'));
      },

      plateMaskKey () {
        const mask = this.fields.vehicle?.plate?.mask;
        return Array.isArray(mask) ? mask.join('|') : (mask || '');
      },

      // Array masks (e.g. PY) crash vue-the-mask with Vuetify — handle those in onPlateInput
      plateMaskDirective () {
        const mask = this.fields.vehicle?.plate?.mask;
        return Array.isArray(mask) ? '' : (mask || '');
      },

      ready () {
        const loaded = this.loaded;
        const config = this.config;
        const setup = this.setup;
        console.log('ready', loaded && config, loaded, config, setup);
        return loaded && !!config && setup;
      },
    },

    watch: {
      loaded (b) {
        if (b) {
          this.$emit('load', this.profile);
        }
      },
      toggle: {
        immediate: true,
        handler (b) {
          if (b) {
            setTimeout(($) => {
              const state = $.sectionState;
              const pending = _.findKey(state, (section, s) => {
                // console.log('section', s, section);
                return section.required&&!section.completed;
              });
              if (!!pending) {
                _.each($.sections, section => {
                  section = false;
                })
                $.sections[pending] = true;
                console.log('pending', pending);
              }
            }, 1500, this);
          }
        }
      },
      sectionState: {
        immediate: true,
        deep: true,
        handler (state) {
          const pending = this.status=='FDB' ? null : _.findKey(state, (section, s) => {
            // console.log('section', s, section);
            return section.required&&!section.completed;
          });
          // if (!!pending) this.sections[pending] = true;
          // console.log('pending', pending);
          this.view.pending = this.loaded&&!_.isNil(pending);
        },
      },

      completion: {
        deep: true,
        handler (completion) {
          this.stepUpdated(completion.percent);
        },
      },

      installed: {
        immediate: true,
        handler (installed) {
          // get Device Info using UAParser
          const deviceInfo = device.getDeviceInfo();
          this.deviceInfo = deviceInfo;
          this.os = `${deviceInfo.manufacturer}/${deviceInfo.model} ${deviceInfo.operatingSystem} ${deviceInfo.osVersion}`;
          console.log('### OS:', JSON.stringify(this.os));
          if (installed) {
            App.getInfo().then(app => {
              console.log('### App build:', JSON.stringify(app.build));
              this.setVersionCode(app.build);
            })
          }
          // Capacitor.Plugins.Mobees.getVersionCode().then(result => {
          //   console.log('### versionCode', result.versionCode);
          //   this.setVersionCode(result.versionCode);
          // });
        },
      },
      'fields.address.zip.value' (zip) {
        console.log('zip', zip);
      },
      'fields.payment.owner' (owner) {
        this.updateRequiredFields();
      },
      'fields.address.country.value': {
        handler (value) {
          this.applyCountryMasks(value);
        },
        immediate: true
      },
      countries (list) {
        if (!_.isEmpty(list) && this.fields.address.country.value) {
          this.applyCountryMasks(this.fields.address.country.value);
        }
      },
    },

    methods: {
      ...services,
      parser,

      applyCountryMasks (value) {
        if (!value) return;
        const locale_code = _.last((this.$i18n.locale || '').split('-')) || '';
        const val = String(value).toLowerCase();
        const country = this.countries.find(c => {
          if (!c?.iso2) return false;
          const iso2Lower = c.iso2.toLowerCase();
          return c.iso2 === value
            || iso2Lower === val
            || (c.name && c.name.toLowerCase() === val)
            || (c.native && c.native.toLowerCase() === val)
            || (locale_code && iso2Lower === locale_code.toLowerCase() && iso2Lower === val);
        });
        if (!country) return;
        console.log('country', country);
        if (country.states) {
          this.fields.address.state.items = country.states;
        }
        if (country.masks) {
          _.each(country.masks, (mask, key) => {
            const field = _.get(this.fields, key);
            if (!field || typeof field !== 'object') return;

            if (mask == null) {
              _.set(this.fields, `${key}.mask`, '');
              if (_.has(field, 'regex')) _.set(this.fields, `${key}.regex`, null);
              if (_.has(field, 'rules')) {
                _.set(this.fields, `${key}.rules`, [(v) => !!v || 'general.mandatory_field']);
              }
              return;
            }

            _.set(this.fields, `${key}.mask`, Array.isArray(mask) ? mask.slice() : mask);

            const pattern = Array.isArray(mask) ? mask[0] : mask;
            if (typeof pattern !== 'string' || !pattern) return;

            const regexSource = this.maskToRegex(mask);
            _.set(this.fields, `${key}.regex`, regexSource ? new RegExp(regexSource) : null);
            _.set(this.fields, `${key}.rules`, [(v, $, regex) => !!v && (!regex || regex.test(v)) || 'general.mandatory_field']);
          });
        } else {
          const fieldsToReset = ['cpf', 'phone', 'address.zip', 'vehicle.plate', 'payment.cpf', 'vehicle.ownerCpf', 'rg.id'];
          _.each(fieldsToReset, (fieldKey) => {
            const field = _.get(this.fields, fieldKey);
            if (field && _.has(field, 'mask')) {
              _.set(this.fields, fieldKey + '.mask', undefined);
              if (_.has(field, 'regex')) {
                _.set(this.fields, fieldKey + '.regex', null);
                _.set(this.fields, fieldKey + '.rules', [(v) => !!v || 'general.mandatory_field']);
              }
            }
          });
        }
        this.setup = true;
      },

      // toggleView (b) {
      //   this.view.toggle = b;
      // },

      toggleBadges (event, key) {
        // console.log(this.badge_view.mini, key);
        this.badge_view.mini = this.badge_view.mini ? false : key==null;
      },

      setVersionCode (versionCode) {
        this.app_version = APP_VERSION + '/' + versionCode;
        this.app_build = versionCode;
        this.version = Object.assign({}, { native: versionCode, web: APP_VERSION });
      },

      onFormFocus (b) {
        this.keyboard = b;
      },

      // toggleHelp (b=true, context='profile') {
      //   this.views.help.toggle = !_.isBoolean(b) || b;
      //   this.views.help.context = context;

      //   if (b) this.trackPage('help', {
      //     hash: context,
      //     title: this.help[context].title
      //   });
      // },
      
      // trackPage (page, subroute) {
      //   const route = subroute ? '/'+page+'/'+subroute.hash : '';
      //   const title = subroute ? this.views[page].title + ': ' + subroute.title : this.views[page].title;
      //   // track analytics
      //   this.$gtag.pageview({
      //     page: route,
      //     title: title + ' | Parceiros'
      //   });
      //   console.log(this.views[page].title + ' | Parceiros');
      // },

      onScroll (e) {
        this.offsetTop = e.target.scrollTop;
        if (!this.view.fullscreen&&this.offsetTop>5) {
          this.toggleFullscreen(true);
          this.$vuetify.goTo(0, {
            container: this.$refs.content,
            duration: 500,
            offset: 0,
            easing: 'easeInOutCubic'
          });
        }
      },

      // modelFilter (item, query) {
      //   item = item.toLowerCase();
      //   query = query.toLowerCase();

      //   return item.indexOf(query) > -1;
      // },


      handleCropperInit () {
        this.toggleUpload = false;
      },

      toggleCropper (b) {
        // this.toggleUpload = b;
        if(b) {
          this.$refs.cropper.remove();
          this.$refs.cropper.chooseFile();
        }else{
          this.toggleUpload = false;
          this.$refs.cropper.remove();
        }
      },

      handleNewImage (){
        this.toggleUpload = true;

        // Fix photo orientation
        const meta = this.cropper.getMetadata();
        meta.orientation = 1;
        this.cropper.applyMetadata(meta);
        console.log(meta);
      },

      prepareImgUpload () {
        this.toggleUpload = false;
        this.fields.photo.value = this.$refs.cropper.generateDataUrl('image/jpeg', 0.8);
        this.profileChanged('photo');
        this.updateProfile();
      },

      onFileTypeMismatch () {
        this.toggleToast(
          true,
          this.$t('errors.invalid_image_format'),
          5000,
          false,
        );
      },

      onFileSizeExceed () {
        this.toggleToast(
          true,
          this.$t('errors.file_too_large'),
          5000,
          false,
        );
      },

      regionFilter (item, query, text) {
        if (_.has(item, 'title')) {
          const normalized = item.title.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
          return new RegExp(query.toLowerCase()).test(normalized);
        }else{
          return false;
        }
      },

      roundExperience () {
        const xp = this.fields.job.experience.value;
        this.fields.job.experience.value = xp > 10 ? _.round(xp / 12) * 12 : xp;
      },

      appLabel (selected) {
        const option = _.find(this.fields.job.apps.items, item => {
          return item.value === selected.app || item.value === selected.text;
        });
        if (option?.text && this.$te(option.text)) return this.$t(option.text);
        return selected.text || selected.app || '';
      },

      updateApps () {
        // update apps_percent
        const apps = this.fields.job.apps.value;
        const options = _.keyBy(this.fields.job.apps.items, 'value');
        const nilled = apps==null||_.isEmpty(apps) ? null : _.some(options, option => {
          const selected = _.indexOf(apps, option.value)>=0;
          return selected&&_.has(option, 'nil')&&option.nil;
        });
        if (nilled) {
          this.$set(this.fields.job.apps, 'value', [_.find(options, ['nil', true]).value]);
        }
        this.fields.job.apps.items = _.map(options, option => {
          const nil = _.has(option, 'nil')&&option.nil;
          return { ...option, disabled: nilled!=null&&(!nil&&nilled||nil&&!nilled) }
        });
        let percent = this.fields.job.apps_percent.value;
        let balance = false;
        const changed = !!apps&&!!percent&&percent.length!=apps.length;
        if (!!apps) {
          if (percent==null||changed) {
            balance = apps.length>1;
            percent = _.map(_.filter(_.clone(this.fields.job.apps.value), (app) => !(_.has(options[app], 'nil')&&options[app].nil)), (app) => {
              return {
                text: options[app].text,
                app,
                value: apps.length==1 ? 100 : null
              }
            });
          }
        }else{
          percent = null;
        }
        this.$set(this.fields.job.apps_percent, 'value', percent);
        this.fields.job.apps_percent.toggle = _.size(percent)>1;
        this.updateAppsPercent(balance, changed&&apps.length==1 ? _.first(apps) : null);
      },

      updateAppsPercent (balance=true, locked=null) {
        let field = this.fields.job.apps_percent.value;
        const controller = this.fields.job.apps_percent;
        const min = _.first(controller.options);
        const total = () => _.sumBy(_.values(field), 'value');
        const equity = (left, ratio, last) => _[last ? 'floor' : 'ceil'](left/min / ratio) * min;
        const unset = () => _.filter(field, ['value', null]).length;
        if (balance&&unset()<=1) {
          field = _.map(field, (selected, i) => {
            const value = selected.value==null ? 
              100-total() : 
              unset()>0||locked.app!=null&&locked.app==selected.app ? 
                selected.value :
                field.length==2 ? equity(100-locked.value, field.length-1, field.length-1==i) : null;
            return { ...selected, value }
          });
          this.$set(this.fields.job.apps_percent, 'value', field);
        }
        this.$nextTick(() => {
          const options = _.map(_.clone(controller.options), (option) => {
            const disabled = unset()<=1 ? false : total()+option>100-(min*(field.length-2));
            return { text: option+'%', value: option, disabled };
          });
          controller.items = options;
          if (locked!=null) this.profileChanged('job.apps_percent');
        });
      },

      sectionIcon (required, completed) {
        return !required||completed ? this.signup&&this.status!='FDB' ? this.icons.mdiCheck : !this.signup&&!this.recentAuth&&!this.ghost ? this.icons.mdiLock : '$expand' : this.icons.mdiAlertCircleOutline;
      },
      
      // sectionRequired (section) {
      //   return this.step.sections[section].required.length > 0;
      // },

      sectionComplete (section) {
        const status = this.status;
        return status!=null && this.step.sections[section].percent==1;
      },

      setField (field, value) {
        _.set(this.fields, field+'.value', value);
        this.profileChanged(field);
      },

      onPlateInput (value) {
        const mask = this.fields.vehicle.plate.mask;
        // Array masks (PY) are applied here — vue-the-mask breaks on them with Vuetify
        if (Array.isArray(mask)) {
          const masked = this.mask(value, mask);
          if (masked !== value) this.fields.vehicle.plate.value = masked;
        }
        this.profileChanged('vehicle.plate');
      },

      profileChanged (field) {
        // log change
        const required = _.flatten(_.map(this.step.sections, section => section.required)).indexOf(field)>=0;
        // console.log('profileChanged', field, required);
        const saved = _.get(this.profile, field);
        const value = _.get(this.fields, field).value;
        const valid = this.validateField(field);
        if ((required&&valid||!required)&&(!_.isEqual(saved, _.isString(value) ? this.rawData(value) : value))&&this.changed.indexOf(field)===-1) {
          this.changed.push(field);
          // console.log(field, 'changed');
        }else if (required&&!valid&&this.changed.indexOf(field)>=0) {
          this.changed.splice(this.changed.indexOf(field), 1);
        }
        if (this.signup && valid) this.setUpdateTimer(true);
        // process colateral changes
        if (field=='job.apps') {
          this.updateApps();
        }
        this.updateCompletion();
      },

      checkUpdates () {
        if (this.changed.length>0) {
          if (!this.saveBtn.loading) this.updateProfile();
        }else{
          this.setUpdateTimer(false);
        }
      },

      loadingFields (toggle) {
        const fields = this.changed;
        _.each(fields, f => {
          _.get(this.fields, f).loading = toggle;
        })
      },

      profileUpdated (fields) {
        this.changed = _.difference(this.changed, fields);
        console.log(fields, this.changed);
      },

      validateField (f) {
        const $ = this;
        const fields = this.fields;
        if (f=='cpf') return true;

        const field = _.get(fields, f);
        if ((f === 'payment.name' || f === 'payment.cpf') && fields.payment?.owner) {
          if (field) {
            if (_.has(field, 'success')) _.set(this.fields, f + '.success', false);
            if (_.has(field, 'error')) _.set(this.fields, f + '.error', false);
          }
          return true;
        }

        const section = _.get(this.step.sections, f.includes('.') && !f.includes('address') ? f.split('.')[0] : 'personal');
        const valid = !!section && section.required.includes(f) && _.has(field, 'rules') && field.rules.length > 0 ? _.every(field.rules, (rule) => {
          let regex = field.regex || null;
          if (!regex && field.mask) {
            const source = this.maskToRegex(field.mask);
            regex = source ? new RegExp(source) : null;
          }
          const invalid = rule(field.value, $, regex)!==true;
          // if (invalid) console.log('invalid', f, section.required);
          return !invalid;
        }) : true;

        if (f=='address.zip'&&valid&&!field.disabled) {
          this.getZipAddress(field.value);
        }else if (f=='address.zip'&&!valid&&!field.disabled) {
          this.checkedAddress = false;
        }

        if (_.has(field, 'success')) {
          _.set(this.fields, f + '.success', valid);
        }
        if (_.has(field, 'error')) {
          _.set(this.fields, f + '.error', !valid);
        }
        return valid;
      },

      toggleFields () {
        // Get consolidated fields from sections (inheritance already processed in step computed property)
        const consolidatedFields = _.flatten(_.map(this.step.sections, section => 
          _.union(section.required || [], section.disabled || [])
        ));
        const all = _.keys(this.dictionary);
        const hidden = _.xor(all, consolidatedFields);
        
        // Hide fields not in consolidated list
        _.each(hidden, key => {
          const field = _.get(this.fields, key);
          if (field && _.has(field, 'toggle')) {
            field.toggle = false;
          }
        });
        
        // Show fields in consolidated list
        _.each(consolidatedFields, key => {
          const field = _.get(this.fields, key);
          if (field && _.has(field, 'toggle')) {
            field.toggle = true;
          }
        });

        // Own account: never show third-party titular fields
        if (this.fields.payment?.name) {
          this.fields.payment.name.toggle = !this.fields.payment.owner && consolidatedFields.includes('payment.name');
        }
        if (this.fields.payment?.cpf) {
          this.fields.payment.cpf.toggle = !this.fields.payment.owner && consolidatedFields.includes('payment.cpf');
        }
      },

      updateRequiredFields () {
        // Get consolidated sections (inheritance already processed in step computed property)
        const sections = this.step.sections;
        if (!sections) return;
        // Apply dynamic field updates based on current state
        const required = ['ACC', 'FDB', 'ENR', 'SIG'].includes(this.status) ? 'required' : 'disabled';
        if (sections.vehicle && this.profile.vehicle?.isOwner==2) {
          sections.vehicle[required].push('vehicle.rental');
          if (this.status=='ACC'||this.status=='FDB') {
            _.pull(sections.vehicle[required], 'vehicle.ownerRelative');
          }else{
            _.pullAll(sections.vehicle[required], ['vehicle.ownerRelative','vehicle.ownerName','vehicle.ownerEmail','vehicle.ownerRg','vehicle.ownerOrg_exp','vehicle.ownerCpf']);
          }
        }else if (sections.vehicle && this.profile.vehicle?.isOwner==0){
          if (this.status=='ACC'||this.status=='FDB') {
            sections.vehicle[required].push('vehicle.ownerRelative');
          }else{
            sections.vehicle[required] = _.union(sections.vehicle[required], ['vehicle.ownerRelative','vehicle.ownerName','vehicle.ownerEmail','vehicle.ownerRg','vehicle.ownerOrg_exp','vehicle.ownerCpf']);
          }
          _.pull(sections.vehicle[required], 'vehicle.rental');
        }
        
        if (sections.payment) {
          if (this.fields.payment.owner) {
            sections.payment.required = _.without(sections.payment.required || [], 'payment.name', 'payment.cpf');
            sections.payment.disabled = _.without(sections.payment.disabled || [], 'payment.name', 'payment.cpf');
          }else{
            sections.payment.required = _.union(sections.payment.required || [], ['payment.name', 'payment.cpf']);
          }
        }

        // Update field states based on consolidated required/disabled lists
        _.each(sections, (section, s) => {
          _.each(section.required, key => {
            const field = _.get(this.fields, key);
            if (field) {
              // Validate and set error state - ensure error property exists
              const isValid = this.validateField(key);
              // Use _.set to ensure the property exists on the nested object
              _.set(this.fields, key + '.error', !isValid);
              if (_.has(field, 'disabled')) field.disabled = false;
            }
          }); 
          _.each(section.disabled, key => {
            const field = _.get(this.fields, key);
            if (field && _.has(field, 'disabled')) {
              field.disabled = true;
            }
          }); 
        });

        this.updateCompletion();
      },

      updateCompletion () {
        this.toggleFields();
        const completion = this.completion.percent;
        this.$nextTick(() => {
          this.stepUpdated(completion);
        });
      },

      stepUpdated (completion=this.completion.percent) {
        let data = null;
        if (this.status=='ACC'&&!this.express) {
          data = { completion };
        }else if (['ENR', 'ACC', 'SIG'].includes(this.status)) {
          data = { profile: completion };
        }
        this.$emit('step-update', data);
      },

      requestEvaluation () {
        const sections = this.step.sections;
        _.each(sections, section => {
          _.map(section.required, (f) => {
            if (f!='cpf') this.profileChanged(f);
          });
        });
        this.$emit('action', 'step1');
      },

      close () {
        // close modal and discard changes
        this.changed = [];
        this.toggleView();
      },

      validatePassword () {
        // validation
        const a = this.password.input.length>=3 ? true : false;
        const b = this.password.input===this.confirmPassword.input ? true : false;
        this.passwordBtn.disabled = !(a&&b);
        return a&&b;
      },

      authUpdated (data) {
        const username = this.rawUsername(data.username || this.user.username);
        this.auth = Object.assign({}, this.auth, {
          username,
          jwt: data.jwtToken,
          token: data.authToken,
          timestamp: new Date(),
          status: true,
        });
        this.dialogs.auth.toggle = false;
      },

      toggleAuth (b, section) {
        if (!this.signup && !this.recentAuth) {
          this.dialogs.auth.toggle = b;
        }
      },

      scrollToSection (section) {
        // setTimeout(vue => {
        //   vue.$vuetify.goTo(vue.$refs[section], {
        //     container: vue.$refs.content,
        //     duration: 500,
        //     offset: 0,
        //     easing: 'easeInOutCubic'
        //   });
        // }, 150, this);
      },

      hasAddress () {
        // const street = 'address.street';
        const city = 'address.city';
        const state = 'address.state';
        const has = _.every([city, state], v => {
          const valid = this.validateField(v);
          return valid;
        })
        return has;
      },

      getZipAddress (zip) {
        if (!zip || (this.fields.address.country.value && this.fields.address.country.value !== 'BR')) return;

        const checked = this.checkedAddress;
        if (!this.hasAddress()&&!this.fields.address.zip.loading) {
          this.fields.address.zip.loading = true;
          // axios(`https://nominatim.openstreetmap.org/search?postalcode=${zip}&format=json&addressdetails=1`)
          axios(`https://viacep.com.br/ws/${zip}/json/`)
          .then(response => {
            console.log('/getZipAddress', response);
            if (!_.has(response.data, 'erro')) {
              const street = response.data.logradouro;
              console.log(street, this.profile.address.street);
              if (street!=this.profile.address.street) {
                this.fields.address.street.value = street;
                // this.profile.address.street = street;
                // this.fields.address.street.disabled = true;
                this.profileChanged('address.street');
              } 
              const neighborhood = response.data.bairro;
              console.log(neighborhood, this.profile.address.neighborhood);
              if (neighborhood!=this.profile.address.neighborhood) {
                this.fields.address.neighborhood.value = neighborhood;
                // this.profile.address.neighborhood = neighborhood;
                // this.fields.address.neighborhood.disabled = true;
                this.profileChanged('address.neighborhood');
              } 
              const city = response.data.localidade;
              console.log(city, this.profile.address.city);
              if (city!=this.profile.address.city) {
                this.fields.address.city.value = city;
                // this.profile.address.city = city;
                // this.fields.address.city.disabled = true;
                this.profileChanged('address.city');
              } 
              const state = response.data.uf;
              console.log(state, this.profile.address.state);
              if (state!=this.profile.address.state) {
                this.fields.address.state.value = state;
                // this.profile.address.state = state;
                // this.fields.address.state.disabled = true;
                this.profileChanged('address.state');
              }
              const country = 'BR';
              if (country!=this.profile.address.country) {
                this.fields.address.country.value = country;
                // this.profile.address.country = 'Brasil';
                // this.fields.address.country.disabled = true;
                this.profileChanged('address.country');
              }

              this.checkedAddress = true;
            }else{
              // this.disableAddress();
            }
          }).finally(() => {
            this.fields.address.zip.loading = false;
          })
        }else{
          // this.disableAddress();
        }
      },

      disableAddress () {
        _.each(['street', 'city', 'state', 'country'], field => {
          this.fields.address[field].disabled = true;
        });
      },

      deleteAccount () {
        if (!this.isOnline) return false;
        this.dialogs.deleteAccount.loading = true;

        this.$api.post('/driver/account/delete', {})
          .then(response => {
            console.log('deleteAccount => ', response);
            if(response.data.retorno==200){
              // success
              this.toggleToast(
                true,
                this.$t('profile.delete_account.success'),
                5000,
                false,
              );
              this.trackEvent('account', 'deleted');
            }else{
              this.toggleToast(
                true,
                this.$t('profile.delete_account.failed'),
                5000,
                false,
              );
              this.trackEvent('account', 'delete error');
            }
          })
          .catch(error => {
            this.handleError(error, this.$t('profile.delete_account.error'), true);
          })
          .finally(() => {
            this.getout();
            this.dialogs.deleteAccount.loading = false;
          })
      },

      getAuth (username, password) {
        if (!this.isOnline) return false;
        this.dialogs.auth.loading = true;
        username = this.rawUsername(username);

        this.$api({
          url: '/driver/auth/login',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            data: {
              username,
              password,
            }
          })
          .then(response => {
            console.log('getAuth => ',response);
            this.auth = Object.assign({}, this.auth, {
              username,
              jwt: response.data.jwtToken,
              token: response.data.authToken,
              timestamp: new Date(),
              status: true,
            });
            this.trackEvent('profile', 'data access auth');
          })
          .catch(error => {
            if (error.response.status === 401) {
              this.toggleToast(
                true,
                this.$t('errors.invalid_credentials'),
                5000,
                false,
              );
              this.trackEvent('profile', 'data access invalid credentials');
            }else{
              this.handleError(error, null, true);
            }
          })
          .finally(() => {
            this.dialogs.auth.toggle = this.dialogs.auth.loading = false;
          });
      },

      updatePassword () {
        if (!this.isOnline) return false;
        if (this.validatePassword()) {
          this.passwordBtn.loading = true;

          const password = this.password.input;

          this.$api
            .put('/driver/auth/password/', {
              password
            })
            .then(response => {
              console.log('updatePassword => ', response);
              if(response.data==201){
                // success
                this.toggleToast(
                  true,
                  'Senha alterada!',
                  5000,
                  false,
                );
                this.password.toggle = false;
                this.trackEvent('profile', 'updated password');
              }else{
                this.toggleToast(
                  true,
                  this.$t('errors.change_password_failed'),
                  5000,
                  false,
                );
                this.trackEvent('profile', 'update password error');
              }
              this.password.input = this.confirmPassword.input = '';
            })
            .catch(error => {
              this.handleError(error, 'Não foi possível alterar a senha', true);
            })
            .finally(() => {
              this.passwordBtn.loading = false;
            })
        }
      },

      togglePasswordRecovery (b) {
        this.views.forgotPassword.toggle = b;

        // this.trackPage('forgotPassword');
      },

      processFields () {
        // iterate through changed fields and format accordingly
        let data = {};
        const changes = this.changed;
        for(var f in changes){
          if(changes[f].indexOf('.')>=0){
            const keys = changes[f].split('.');
            const [section, field] = keys;
            switch (changes[f]) {
              case 'rg.issuer':
                data[this.translateNotation(changes[f])] = this.fields[section][field].value.toUpperCase();
                break;
              case 'rg.id':
              case 'vehicle.ownerRg':
              case 'vehicle.ownerCpf':
              case 'address.zip':
              case 'payment.cpf':
                data[this.translateNotation(changes[f])] = this.rawData(this.fields[section][field].value);
                break;
              case 'vehicle.plate':
                data[this.translateNotation(changes[f])] = this.rawData(this.fields[section][field].value).toUpperCase();
                break;
              case 'job.rating':
                data[this.translateNotation(changes[f])] = parseFloat(this.fields[section][field].value);
                break;
              case 'job.apps':
              case 'job.products':
              case 'vehicle.fuel':
                data[this.translateNotation(changes[f])] = this.fields[section][field].value.toString();
                break;
              case 'vehicle.model':
                let model = this.fields.vehicle.model.value;
                if (model=='Outro') {
                  model = 'Outros Outros';
                }
                const combo = model.split(' ');
                data[this.translateNotation('vehicle.brand')] = combo.shift();
                data[this.translateNotation(changes[f])] = combo.join(' ');
                break;
              default:
                data[this.translateNotation(changes[f])] = this.fields[section][field].value;
            }
          }else{
            switch (changes[f]) {
              case 'cpf':
              case 'rg':
              case 'phone':
                data[this.translateNotation(changes[f])] = this.rawData(this.fields[changes[f]].value);
                break;
              case 'birthdate':
                data[this.translateNotation(changes[f])] = this.$moment(this.fields[changes[f]].value,'DD-MM-YYYY').utc().format();
                break;
              case 'email':
                this.auth.sms = false;
                this.fields.email.hint = '';
              case 'status':
              case 'documentos':
                // PREVENT STATUS UPDATE
                break;
              default:
                data[this.translateNotation(changes[f])] = this.fields[changes[f]].value;
            }
          }
        }
        console.log(data);
        return {
          data: data,
          fields: JSON.parse(JSON.stringify(changes))
        }
      },

      updateProfile () {
        if (!this.isOnline) return false;

        this.saveBtn.loading = true;
        if (this.signup) this.loadingFields(true);

        const processed = this.processFields();
        if (!this.ghost) {
          processed.data.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          processed.data.locale = this.$i18n.locale;
        }

        this.$api
          .post('/driver/profile',{
            data: processed.data
          })
          .then(response => {
            console.log('updateProfile => ', response);
            if(response.data==200){
              // success
              if (!this.signup) {
                this.toggleToast(
                  true,
                  'Dados salvos com sucesso!',
                  5000,
                  false,
                );
              }else{
                this.loadingFields(false);
              }
              this.profileUpdated(processed.fields);
              this.trackEvent('profile', 'updated profile');
            }else{
              if (!this.signup) {
                this.toggleToast(
                  true,
                  this.$t('errors.save_data_failed'),
                  5000,
                  false,
                );
              }else{
                if (_.isNil(this.errors)) this.errors = [];
                this.errors.push({
                  action: 'updateProfile',
                  status: response.data.retorno
                });
                if (this.errors.length>=5) {
                  this.errors.length = 0;
                  window.location.reload();
                  return false;
                }
              }
              this.trackEvent('profile', 'update profile error');
            }
          })
          .catch(error => {
            this.handleError(error);
          })
          .finally(() => {
            this.loadingFields(false);
            this.saveBtn.loading = false;
          })
      },

      getProfile (onSuccess=null) { 
        this.view.loading = true;

        if (this.auth.token==null) this.getout();

        this.$api
          .get('/driver/profile')
          .then(response => {
            console.log('getProfileInfo => ',response);
            // check if local data is more complete
            const profile = this.parser(response.data, this);
            this.status = response.data.status_motorista;
            this.statusChange = response.data.dt_Status;

            const fields = this.fields;
            setTimeout(() => {
              _.each(fields, (field, f) => {
                if (_.has(field, 'value')) {
                  this.fields[f].value = profile[f];
                }else{
                  const section = field;
                  const s = f;
                  _.each(section, (field, f) => {
                    if (_.has(field, 'value')) {
                      let value = profile[s][f];
                      let maskToUse = field.mask;
                      if (s === 'address' && f === 'zip' && profile.address && profile.address.country) {
                        const country = this.countries.find(c => c.iso2 === profile.address.country || (c.iso2 && c.iso2.toLowerCase() === (profile.address.country || '').toLowerCase()));
                        if (country && country.masks && country.masks['address.zip']) {
                          maskToUse = country.masks['address.zip'];
                        }
                      }
                      if (s === 'vehicle' && f === 'plate' && profile.address && profile.address.country) {
                        const country = this.countries.find(c => c.iso2 === profile.address.country || (c.iso2 && c.iso2.toLowerCase() === (profile.address.country || '').toLowerCase()));
                        if (country?.masks?.['vehicle.plate'] != null) {
                          maskToUse = country.masks['vehicle.plate'];
                        }
                      }
                      this.$set(this.fields[s][f], 'value', maskToUse ? this.mask(value, maskToUse) : value);
                      // if (f=='zip'||f=='cpf') console.log('mask', s, f, this.fields[s][f].value);
                    }
                  });
                }
              })
              // fields.cpf.value = profile.cpf ? this.mask(profile.cpf, fields.cpf.mask) : profile.cpf;
            }, 500);

            if (['ACT', 'EXP'].includes(this.status)&&_.indexOf(profile.job.apps, 'TAXIRIO')>=0) this.fields.job.apps.disabled = true;

            this.profile = Object.assign(_.cloneDeep(this.profile), profile);
            this.$nextTick(() => {
              this.changePaymentOwner();
              this.updateRequiredFields();
              this.updateApps();

              if (this.hasAddress()&&response.data.cep!=null) this.getZipAddress(response.data.cep);
              this.$nextTick(() => {
                const pending = _.some(this.sectionState, { required: true, completed: false });
                this.view.pending = pending;
                this.loaded = true;
              });
            });

            const version = response.data.vs_app;
            const os = response.data.vs_so;
            this.profile.os = os;
            this.profile.app_version = this.app_version;
            if (!this.ghost&&(version!=this.app_version||os!=this.os)) {
              console.log('app version: ', this.app_version);
              console.log('OS: ', this.os);
              this.setAppVersion(this.app_version, this.os);
            }

            if (!this.ghost) {
              this.syncLocaleTimezone(response.data);
            }

            // get updated GPS permission
            // this.hasLocation();

            // TODO: (!) get updated PUSH permission
            // this.hasPush();
            
            // load list of banks 
            this.getBankList();

            if (onSuccess!=null) onSuccess(response.data);
          })
          .catch(error => {
            if(error?.response?.status==401) {
              this.getout();
              this.toggleToast(
                true,
                this.$t('errors.session_expired'),
                5000,
                false
              );
            }else{
              this.view.loader = setTimeout(($) => {
                this.view.loader = null;
                $.getProfile();
              }, 7000, this);
              this.handleError(error);
            }
          })
          .finally(() => {
            this.view.loading = false;
          });
      },

      getBankList () {
        this.$api
          .get('/driver/bancos?format=json')
          .then(response => {
            console.log('getBankList => ', response);
            if(response.data.length>0){
              this.bankList = response.data.map(function(item){
                return { id: item.id_banco, code: item.cod_banco, title: item.nm_banco };
              });
            }else{
              // this.toggleToast(
              //   true,
              //   'Erro na consulta de bancos',
              //   5000,
              //   false,
              // );
            }
          })
          .catch(error => {
            this.handleError(error);
          });
      },

      changePaymentOwner () {
        this.fields.payment.name.toggle = !this.fields.payment.owner;
        this.fields.payment.cpf.toggle = !this.fields.payment.owner;
        if (this.fields.payment.owner) {
          _.each(['name', 'cpf'], field => {
            this.fields.payment[field].value = null;
            this.fields.payment[field].success = false;
            this.fields.payment[field].error = false;
            this.profileChanged(`payment.${field}`);
          });
        }
        this.updateRequiredFields();
      },

      setAppVersion (version, os) {
        if (this.ghost) return;

        this.$api({
          url: '/driver/app/version',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            data: {
              version,
              os
            }
          })
          .then(response => {
            console.log('setAppVersion => ',response);
            if(response.data.retorno==200){
              localStorage.setItem("app_version", version);
              this.trackEvent('app version', 'updated');
            }else{
              this.getout();
              this.toggleToast(
                true,
                this.$t('errors.session_expired'),
                5000,
                false
              );
            }
          })
          .catch(error => {
            // this.handleError(error);
          })
          .finally(() => {
            
          });
      },

      async syncLocaleTimezone (db) {
        if (this.ghost || !db) return;

        try {
          const country = db.pais || this.profile?.address?.country || null;
          const dbLocale = db.locale || null;
          const stored = await this.getStorage('locale');

          // Prefer DB / stored locale; otherwise resolve with country before comparing
          await this.setLocale(dbLocale || stored || null, country);

          const locale = this.$i18n.locale;
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const dbTimezone = db.timezone || null;

          if (locale) await this.setStorage('locale', locale);

          const data = {};
          if (locale && String(locale).toLowerCase() !== String(dbLocale || '').toLowerCase()) {
            data.locale = locale;
          }
          if (timezone && timezone !== dbTimezone) {
            data.timezone = timezone;
          }
          if (_.isEmpty(data)) return;

          const response = await this.$api.post('/driver/profile', { data });
          console.log('syncLocaleTimezone => ', response, data);
          this.trackEvent('profile', 'synced locale timezone');
        } catch (error) {
          console.warn('syncLocaleTimezone failed', error);
        }
      },

      async onLocaleChange (locale) {
        if (!locale || this.ghost) return;
        if (String(locale).toLowerCase() === String(this.$i18n.locale || '').toLowerCase()) return;

        this.localeLoading = true;
        try {
          await this.setLocale(locale);
          await this.setStorage('locale', locale);

          const response = await this.$api.post('/driver/profile', { data: { locale } });
          console.log('onLocaleChange => ', response);
          this.trackEvent('profile', 'updated locale');
        } catch (error) {
          console.warn('onLocaleChange failed', error);
        } finally {
          this.localeLoading = false;
        }
      },

      setUpdateTimer (toggle) {
        if (toggle) {
          if (this.timer.controller==null) {
            this.timer.controller = setInterval(vue => {
              vue.checkUpdates();
            }, 3000, this);
          }else{
            clearInterval(this.timer.controller);
            this.timer.controller = setInterval(vue => {
              vue.checkUpdates();
            }, 1500, this);
          }
        }else{
          clearInterval(this.timer.controller);
          this.timer.controller = null;
        }
      },

      requestGpsPermission () {
        if (this.installed&&!this.profile.gps) {
          // Capacitor.Plugins.Mobees.requestGpsPermission().then(() => {
            Capacitor.Plugins.Permissions.query({ name: "geolocation" }).then((result) => {
                this.profile.gps = (result.state=='granted');

                this.$api.post('/driver/geo/permission', {
                  permitted: this.profile.gps
                })
                .then(response => {
                  console.log('setGeolocationPermission => ', response);
                  if(response.data.retorno==200){
                    // saved
                    this.trackEvent('geolocation', this.profile.gps ? 'enabled' : 'disabled');
                  }else{
                    this.trackEvent('geolocation', 'set state error');
                  }
                });
                setTimeout(vue => {
                  vue.fields.gps.loading = false; 
                }, 500, this);
          }).catch(() => {
              setTimeout(vue => {
                vue.fields.gps.loading = false; 
              }, 500, this);
              // TODO Error Permissions.query
            });
          // }).catch(() => {
          //   // TODO Error MobeesCapacitorPlugin.requestGpsPermission
          // });
        }
      },

      // hasLocation () {
      //   if (this.installed) {
      //     const username = this.rawUsername(this.username);
      //     const token = this.auth.token;

      //     Capacitor.Plugins.Permissions.query({ name: "geolocation" }).then((result) => {
      //       const enabled = (result.state=='granted');

      //       if(this.profile.gps!=enabled){
      //         this.profile.gps = enabled;

      //         this.$api.post('/driver/geo/permission', {
      //           permitted: enabled
      //         })
      //         .then(response => {
      //           console.log('setGeolocationPermission => ', response);
      //           if(response.data.retorno==200){
      //             // saved
      //             this.trackEvent('geolocation', enabled ? 'enabled' : 'disabled');
      //           }else{
      //             this.trackEvent('geolocation', 'set state error');
      //           }
      //         });
      //       }
      //     }).catch(() => {
      //       // TODO handle native access error
      //     });
      //   }
      // },

      // hasPush () {
      //   if (this.installed) {
      //     const username = this.rawUsername(this.username);
      //     const token = this.auth.token;

      //     Capacitor.Plugins.Permissions.query({ name: "notifications" }).then((result) => { 
      //       const enabled = (result.state=='granted');

      //       if(this.profile.push!=enabled){
      //         this.profile.push = enabled;
              
      //         this.$api.post('/driver/push/permission', {
      //           permitted: enabled
      //         })
      //         .then(response => {
      //           console.log('setPushPermission => ', response);
      //           if(response.data.retorno==200){
      //             // saved
      //             this.trackEvent('notification', enabled ? 'enabled' : 'disabled');
      //           }else{
      //             this.trackEvent('notification', 'set state error');
      //           }
      //         });
      //       }
      //     }).catch(error => {
      //       // Error from Capacitor plugin
      //       console.log('Error getting notification plugin from Capacitor');
      //     });
      //   }
      // },

      outdatedVersion (version) {
        return version.split('.');
      }
    },

    mounted () {
      // this.getProfile();
      // this.updateCompletion();

      this.getStorage('ghost').then((value) => {
        this.ghost = value;
      });

      this.fields.job.experience.max = this.$moment().diff('2014-01-01', 'months')

      if (this.auth.sms) this.fields.email.error = true;

      // check app latest version
      // const env = process.env.VUE_APP_MODE=='production' ? '' : '.stg';
      // axios(`https://parceiros${env}.mobees.com.br/manifest.json`).then(response => {
      //   console.log(response);
      // });

      // load cities
      const c = _.orderBy(_.map(cities, city => {
        return {
          title: city.Nome,
          group: 'Outras regiões'
        }
      }), ['title'], ['asc']);
      this.fields.job.region.items = _.concat(this.fields.job.region.items, c);
    },
    directives: {
      Scroll,
      mask: {
        bind (el, binding) {
          applyMask(el, binding);
        },
        update (el, binding) {
          if (binding.value === binding.oldValue) return;
          if (
            Array.isArray(binding.value)
            && Array.isArray(binding.oldValue)
            && binding.value.length === binding.oldValue.length
            && binding.value.every((v, i) => v === binding.oldValue[i])
          ) return;
          applyMask(el, binding);
        },
      },
    },
  }

  function applyMask (el, binding) {
    if (!binding.value) return;

    // vue-the-mask mutates array masks via .sort() — always pass a copy
    const value = Array.isArray(binding.value) ? binding.value.slice() : binding.value;
    const run = (target) => {
      try {
        mask(target, Object.assign({}, binding, { value }));
      } catch (error) {
        console.warn('v-mask', error);
      }
    };

    const input = el.tagName === 'INPUT' ? el : el.querySelector('input');
    if (input) {
      run(el);
      return;
    }

    // Vuetify text fields may not have the inner input on first bind
    requestAnimationFrame(() => run(el));
  }
</script>
